import Vue from "vue";
import Vuex from "vuex";
import client from "@/api/client";
import { OrderManager } from "@/methods/order_manager";
import session from "@/methods/session_storage";
import moment from "moment-timezone";
import router from "@/router/router.js";
import adminManager from "@/methods/admin_manager";

Vue.use(Vuex);

// Helper to wrap dispatch safely and handle API errors
const createSafeDispatch = (commit, dispatch) => {
  return (action) => {
    commit("INCREMENT_LOADING_OPERATIONS");
    return dispatch(action)
      .catch(error => {
        console.error(`Error in ${action}:`, error);
        return Promise.resolve();
      })
      .finally(() => {
        commit("DECREMENT_LOADING_OPERATIONS");
      });
  };
};

// Helper: whether a service is D+1 using backend flag is_d_plus_one
// Combined orders: use import.is_d_plus_one or export.is_d_plus_one
// Single orders: use order.is_d_plus_one (derived from !flag_extra_day)
const isServiceDPlusOne = (orderPart) => {
  return orderPart?.is_d_plus_one === true;
};

// Helpers for review changes
const buildSupplierChangesUrl = (reviewChanges, action) => {
  if (reviewChanges.export_id && reviewChanges.import_id) {
    return `/services/${reviewChanges.import_change_id}/${reviewChanges.export_change_id}/supplier/${action}`;
  }
  return `/service/${reviewChanges.id}/supplier/${action}`;
};

const buildReviewChangesUrl = (reviewChanges, approve) => {
  const action = approve ? "apply" : "deny";
  
  switch (reviewChanges.type) {
    case "supplier_changes":
      return buildSupplierChangesUrl(reviewChanges, action);
    case "break_combination_changes":
      return `/route/break/${reviewChanges.id}/${action}`;
    case "change_routes":
      return `/routes/${reviewChanges.id}/${action}`;
    case "availability_changes":
      return `/suppliers/availability/${reviewChanges.id}/${action}`;
    default:
      return "";
  }
};

const handleReviewChangesResponse = (status, content) => {
  const statusCode = Number(status);
  if (statusCode === 201) {
    return { success: true };
  }
  if (statusCode === 409) {
    return { 
      success: false, 
      blocked: content?.blocked || "El modelo está resolviendo. La operación está bloqueada momentáneamente" 
    };
  }
  return { success: false };
};

const store = new Vuex.Store({
  plugins: [
    (store) => {
      store.$router = router;
    },
  ],
  state: {
    data_interval: null,
    hour_data_interval: null,
    last_resolution: {},
    last_resolution_time: "",
    orderManager: { orders: { confirmed: [], nonConfirmed: [], exported: [] } },
    ports: [],
    management_areas: [],
    all_ports: [],
    all_management_areas: [],
    areas: [],
    preferred_carriers: [],
    preferred_carriers_logistical: [],
    delegations: [],
    delegations_ports: [],
    truck_plates: [],
    truck_driver_holidays: [],
    supplier_zone_changes: [],
    orders_dashboard_info: {},
    savings_dashboard_info: {},
    suppliers_dashboad_info: {},
    suppliers_occupation: {},
    containers: [],
    coordinates: [],
    clients: [],
    alarms: [],
    selected_management_areas: [],
    selected_containers: [],
    selected_suppliers: [],
    selected_order_id: "",
    selected_dates: [],
    selected_port_for_historic_dashboard: [],
    selected_supplier_for_historic_dashboard: [],
    hide_extra_day_orders: false,
    user: {},
    users: [],
    role: [],
    roles: { 1: "DISPATCHER", 2: "SUPERVISOR", 3: "ADMIN", 4: "HISTORIC" },
    profileMode: null,
    break_combination_reasons: [],
    change_supplier_reasons: [],
    change_combination_reasons: [],
    preferred_carrier_motives: [],
    review_changes: null,
    review_changes_formatted: [],
    coordinate_changes_formatted: [],
    supplier_area: [],
    urgent_services: { orders: [] },
    all_suppliers: [],
    port_metrics: [],
    supplier_metrics: [],
    isLoading: false, 
    loadingOperations: 0,
    show_long_term_orders: false,
    long_term_orders: [],
    supplier_availability_motives: [],
    // Access control for restricted endpoints
    // null = not checked yet, true = allowed, false = denied
    access_control: {
      preferred_carriers: null,
      preferred_carriers_logistical: null,
      users: null,
      delegations: null,
      delegations_ports: null,
      truck_plates: null,
      truck_driver_holidays: null,
      supplier_zone_changes: null,
      coordinates: null,
      supplier_info: null,
      areas: null,
      all_ports: null,
      all_management_areas: null,
      review_changes: null,
      preferred_carrier_motives: null,
      supplier_availability_motives: null,
    },
    // Whether initial access checks finished so later 403s do not hide tabs incorrectly
    access_control_verified: false,
  },
  getters: {
    getRole: (state) => {
      // Default to first role, or empty string if none
      return state.role.length > 0 ? state.roles[state.role[0]] : "";
    },
    getUserRoles: (state) => {
      // Role names the user has access to
      return state.role.map(roleId => state.roles[roleId]).filter(Boolean);
    },
    hasRole: (state) => (roleId) => {
      // Whether the user has a specific role
      return state.role.includes(roleId);
    },
    hasAnyRole: (state) => (roleIds) => {
      // Whether the user has any of the given roles
      return roleIds.some(roleId => state.role.includes(roleId));
    },
    canAccessProfile: (state) => (profileName) => {
      // Whether the user can access a given profile
      const roleMapping = {
        'DISPATCHER': 1,
        'SUPERVISOR': 2,
        'ADMIN': 3,
        'HISTORIC': 4
      };
      return state.role.includes(roleMapping[profileName]);
    },
    getProfileMode: (state, getters) => {
      return state.profileMode ? state.profileMode : getters.getRole;
    },
    getLastResolution: (state) => {
      return state.last_resolution;
    },
    getLastResolutionTime: (state) => {
      if (!state.last_resolution_time?.last_solution) {
        return "No hay datos";
      }

      const utcDate = moment.utc(state.last_resolution_time.last_solution);
      const timeZone = moment.tz.guess();
      const localDate = utcDate.tz(timeZone);
      const localTimeString = localDate.format("HH:mm");

      return localTimeString;
    },
    getEndResolutionTime: (state) => {
      if (!state.last_resolution_time?.end_solution) {
        return "No hay datos";
      }

      const newDate = new Date(state.last_resolution_time.end_solution);
      const localTimeString = newDate.toLocaleString("es-ES", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });

      return localTimeString;
    },
    getStartResolutionTime: (state) => {
      if (!state.last_resolution_time?.start_solution) {
        return "No hay datos";
      }

      const newDate = new Date(state.last_resolution_time.start_solution);
      const localTimeString = newDate.toLocaleString("es-ES", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });

      return localTimeString;
    },
    getConfirmedOrders: (state) => {
      const selectedManagementAreas = state.selected_management_areas;
      const selectedProviders = state.selected_suppliers;
      const selectedContainers = state.selected_containers;
      const selectedOrderId = state.selected_order_id;

      return state.orderManager.orders.confirmed
        .filter((order) => {
          const orderProviderId = order.supplier_id;
          const orderContainerId =
            order.type === "export"
              ? order.export.container_type
              : order.import.container_type;
          let orderManagementAreas;
          if (order.type === "combined") {
            orderManagementAreas = [
              order.import.id_management_area,
              order.export.id_management_area,
            ];
          } else if (order.type === "export") {
            orderManagementAreas = [order.export.id_management_area];
          } else {
            orderManagementAreas = [order.import.id_management_area];
          }

          return (
            (selectedProviders?.length === 0 ||
              selectedProviders?.includes(orderProviderId)) &&
            (selectedContainers?.length === 0 ||
              selectedContainers?.includes(orderContainerId)) &&
            (selectedOrderId === "" ||
              (order.type === "combined" &&
                (order.import.id === selectedOrderId ||
                  order.export.id === selectedOrderId ||
                  order.import.commercial_number === selectedOrderId ||
                  order.export.commercial_number === selectedOrderId)) ||
              (order.type !== "combined" &&
                (order.id === selectedOrderId ||
                  order[order.type].commercial_number === selectedOrderId))) &&
            (selectedManagementAreas?.length === 0 ||
              orderManagementAreas.some((orderManagementArea) =>
                selectedManagementAreas.includes(orderManagementArea)
              )) &&
            (
              // Show long-term orders when show_long_term_orders is true, without extra-day flag
              (order.long_term_plan && state.show_long_term_orders) ||
              // For normal (non long-term) orders, apply extra-day filter
              (!order.long_term_plan && (!order.flag_extra_day || state.hide_extra_day_orders))
            )
          );
        })
        .map((order) => {
          return {
            ...order,
            checked: order.checked ? order.checked : false,
          };
        });
    },
    getNonConfirmedOrders: (state) => {
      const selectedManagementAreas = state.selected_management_areas;
      const selectedProviders = state.selected_suppliers;
      const selectedContainers = state.selected_containers;
      const selectedOrderId = state.selected_order_id;

      return state.orderManager.orders.nonConfirmed
        .filter((order) => {
          const orderProviderId = order.supplier_id;
          const orderContainerId =
            order.type === "export"
              ? order.export.container_type
              : order.import.container_type;
          let orderManagementAreas;
          if (order.type === "combined") {
            orderManagementAreas = [
              order.import.id_management_area,
              order.export.id_management_area,
            ];
          } else if (order.type === "export") {
            orderManagementAreas = [order.export.id_management_area];
          } else {
            orderManagementAreas = [order.import.id_management_area];
          }

          return (
            (selectedProviders?.length === 0 ||
              selectedProviders?.includes(orderProviderId)) &&
            (selectedContainers?.length === 0 ||
              selectedContainers?.includes(orderContainerId)) &&
            (selectedOrderId === "" ||
              (order.type === "combined" &&
                (order.import.id === selectedOrderId ||
                  order.export.id === selectedOrderId ||
                  order.import.commercial_number === selectedOrderId ||
                  order.export.commercial_number === selectedOrderId)) ||
              (order.type !== "combined" &&
                (order.id === selectedOrderId ||
                  order[order.type].commercial_number === selectedOrderId))) &&
            (selectedManagementAreas?.length === 0 ||
              orderManagementAreas.some((orderManagementArea) =>
                selectedManagementAreas.includes(orderManagementArea)
              )) &&
            (
              // Show long-term orders when show_long_term_orders is true, without extra-day flag
              (order.long_term_plan && state.show_long_term_orders) ||
              // For normal (non long-term) orders, apply extra-day filter
              (!order.long_term_plan && (!order.flag_extra_day || state.hide_extra_day_orders))
            )
          );
        })
        .map((order) => ({
          ...order,
          checked: order.checked ? order.checked : false,
        }));
    },
    getExportedOrders: (state) => {
      const selectedManagementAreas = state.selected_management_areas;
      const selectedProviders = state.selected_suppliers;
      const selectedContainers = state.selected_containers;
      const selectedOrderId = state.selected_order_id;
      return state.orderManager.orders.exported
        .filter((order) => {
          const orderProviderId = order.supplier_id;
          const orderContainerId =
            order.type === "export"
              ? order.export.container_type
              : order.import.container_type;
          let orderManagementAreas;
          if (order.type === "combined") {
            orderManagementAreas = [
              order.import.id_management_area,
              order.export.id_management_area,
            ];
          } else if (order.type === "export") {
            orderManagementAreas = [order.export.id_management_area];
          } else {
            orderManagementAreas = [order.import.id_management_area];
          }

          return (
            (selectedProviders?.length === 0 ||
              selectedProviders?.includes(orderProviderId)) &&
            (selectedContainers?.length === 0 ||
              selectedContainers?.includes(orderContainerId)) &&
            (selectedOrderId === "" ||
              (order.type === "combined" &&
                (order.import.id === selectedOrderId ||
                  order.export.id === selectedOrderId ||
                  order.import.commercial_number === selectedOrderId ||
                  order.export.commercial_number === selectedOrderId)) ||
              (order.type !== "combined" &&
                (order.id === selectedOrderId ||
                  order[order.type].commercial_number === selectedOrderId))) &&
            (selectedManagementAreas?.length === 0 ||
              orderManagementAreas.some((orderManagementArea) =>
                selectedManagementAreas.includes(orderManagementArea)
              )) &&
            (
              // Show long-term orders when show_long_term_orders is true, without extra-day flag
              (order.long_term_plan && state.show_long_term_orders) ||
              // For normal (non long-term) orders, apply extra-day filter
              (!order.long_term_plan && (!order.flag_extra_day || state.hide_extra_day_orders))
            )
          );
        })
        .map((order) => ({
          ...order,
          checked: order.checked ? order.checked : false,
        }));
    },
    getOrderIds: (state) => {
      let orderIds = state.orderManager.getOrderIds();
      let commercialNumbers = state.orderManager.getCommercialNumbers();
      const urgentOrderIds = state.urgent_services.getUrgentOrderIds();
      const urgentCommercialNumbers = state.urgent_services.getUrgentCommercialNumbers();
      
      // Merge all IDs and commercial numbers and dedupe
      const allIds = [...orderIds, ...commercialNumbers, ...urgentOrderIds, ...urgentCommercialNumbers];
      return [...new Set(allIds)]; // Quitar duplicados
    },
    getConfirmedOrdersCount: (state, getters) => {
      let sum = 0;
      if (getters.getProfileMode == "DISPATCHER") {
        getters.getConfirmedOrders.forEach((order) => {
          if (order.type === "combined") {
            // With "show extra days" on, count all services
            // Otherwise only count D+1 (tomorrow) services
            if (state.hide_extra_day_orders) {
              sum += 2; // Count both services when showing extra days
            } else {
              if (isServiceDPlusOne(order.import)) {
                sum++;
              }
              if (isServiceDPlusOne(order.export)) {
                sum++;
              }
            }
          } else {
            sum++;
          }
        });
      } else {
        sum = state.orders_dashboard_info.total_confirmed;
      }
      return sum;
    },
    getNonConfirmedOrdersCount: (state, getters) => {
      let sum = 0;
      if (getters.getProfileMode == "DISPATCHER") {
        getters.getNonConfirmedOrders.forEach((order) => {
          if (order.type === "combined") {
            // With "show extra days" on, count all services
            // Otherwise only count D+1 (tomorrow) services
            if (state.hide_extra_day_orders) {
              sum += 2; // Count both services when showing extra days
            } else {
              if (isServiceDPlusOne(order.import)) {
                sum++;
              }
              if (isServiceDPlusOne(order.export)) {
                sum++;
              }
            }
          } else {
            sum++;
          }
        });
      } else {
        sum = state.orders_dashboard_info.total_unconfirmed;
      }
      return sum;
    },
    getExportedOrdersCount: (state, getters) => {
      let sum = 0;
      if (getters.getProfileMode == "DISPATCHER") {
        getters.getExportedOrders.forEach((order) => {
          if (order.type === "combined") {
            // With "show extra days" on, count all services
            // Otherwise only count D+1 (tomorrow) services
            if (state.hide_extra_day_orders) {
              sum += 2; // Count both services when showing extra days
            } else {
              if (isServiceDPlusOne(order.import)) {
                sum++;
              }
              if (isServiceDPlusOne(order.export)) {
                sum++;
              }
            }
          } else {
            sum++;
          }
        });
      } else {
        sum = state.orders_dashboard_info.total_exported;
      }
      return sum;
    },
    getTotalOrdersCount: (state, getters) => {
      if (getters.getProfileMode == "DISPATCHER") {
        // Get all orders
        const allOrders = [
          ...getters.getExportedOrders,
          ...getters.getNonConfirmedOrders,
          ...getters.getConfirmedOrders
        ];

        // Contar total de servicios
        // With "show extra days" on, count all services
        // Otherwise only count combined-order services that are D+1
        let sum = 0;
        allOrders.forEach(order => {
          if (order.type === "combined") {
            if (state.hide_extra_day_orders) {
              sum += 2; // Count both services when showing extra days
            } else {
              if (isServiceDPlusOne(order.import)) {
                sum++;
              }
              if (isServiceDPlusOne(order.export)) {
                sum++;
              }
            }
          } else {
            sum++;
          }
        });
        return sum;
      } else {
        return state.orders_dashboard_info.total_orders;
      }
    },
    getImportOrdersCount: (state, getters) => {
      let sum = 0;
      if (getters.getProfileMode == "DISPATCHER") {
        getters.getExportedOrders.forEach((order) => {
          if (order.type === "import") {
            sum++;
          } else if (order.type === "combined") {
            // With "show extra days" on, count all imports
            // Otherwise only count import if it is D+1
            if (state.hide_extra_day_orders || isServiceDPlusOne(order.import)) {
              sum++;
            }
          }
        });
        getters.getNonConfirmedOrders.forEach((order) => {
          if (order.type === "import") {
            sum++;
          } else if (order.type === "combined") {
            if (state.hide_extra_day_orders || isServiceDPlusOne(order.import)) {
              sum++;
            }
          }
        });
        getters.getConfirmedOrders.forEach((order) => {
          if (order.type === "import") {
            sum++;
          } else if (order.type === "combined") {
            if (state.hide_extra_day_orders || isServiceDPlusOne(order.import)) {
              sum++;
            }
          }
        });
      } else {
        sum = state.orders_dashboard_info.total_imports;
      }
      return sum;
    },
    getExportOrdersCount: (state, getters) => {
      let sum = 0;
      if (getters.getProfileMode == "DISPATCHER") {
        getters.getExportedOrders.forEach((order) => {
          if (order.type === "export") {
            sum++;
          } else if (order.type === "combined") {
            // With "show extra days" on, count all exports
            // Otherwise only count export if it is D+1
            if (state.hide_extra_day_orders || isServiceDPlusOne(order.export)) {
              sum++;
            }
          }
        });
        getters.getNonConfirmedOrders.forEach((order) => {
          if (order.type === "export") {
            sum++;
          } else if (order.type === "combined") {
            if (state.hide_extra_day_orders || isServiceDPlusOne(order.export)) {
              sum++;
            }
          }
        });
        getters.getConfirmedOrders.forEach((order) => {
          if (order.type === "export") {
            sum++;
          } else if (order.type === "combined") {
            if (state.hide_extra_day_orders || isServiceDPlusOne(order.export)) {
              sum++;
            }
          }
        });
      } else {
        sum = state.orders_dashboard_info.total_exports;
      }

      return sum;
    },
    getCombinedOrdersCount: (state, getters) => {
      let sum = 0;
      if (getters.getProfileMode == "DISPATCHER") {
        getters.getExportedOrders.forEach((order) => {
          if (order.type === "combined") {
            // With "show extra days" on, count all services
            // Otherwise only count D+1 services
            if (state.hide_extra_day_orders) {
              sum += 2; // Count both services when showing extra days
            } else {
              if (isServiceDPlusOne(order.import)) {
                sum++;
              }
              if (isServiceDPlusOne(order.export)) {
                sum++;
              }
            }
          }
        });
        getters.getNonConfirmedOrders.forEach((order) => {
          if (order.type === "combined") {
            if (state.hide_extra_day_orders) {
              sum += 2;
            } else {
              if (isServiceDPlusOne(order.import)) {
                sum++;
              }
              if (isServiceDPlusOne(order.export)) {
                sum++;
              }
            }
          }
        });
        getters.getConfirmedOrders.forEach((order) => {
          if (order.type === "combined") {
            if (state.hide_extra_day_orders) {
              sum += 2;
            } else {
              if (isServiceDPlusOne(order.import)) {
                sum++;
              }
              if (isServiceDPlusOne(order.export)) {
                sum++;
              }
            }
          }
        });
      } else {
        sum = state.orders_dashboard_info.total_combinations;
      }
      return sum;
    },
    getCombinedOrdersPercentage: (state, getters) => {
      const percentage = Math.round(
        (getters.getCombinedOrdersCount / getters.getTotalOrdersCount) * 100
      );
      const difference = 100 - percentage;
      const result = [percentage, difference];
      return result;
    },
    getOrdersInfo: (state) => {
      return state.orders_dashboard_info;
    },
    getTotalkilometersSaved: (state) => {
      return (
        parseInt(state.savings_dashboard_info.base_km || 0) -
        parseInt(state.savings_dashboard_info.km || 0)
      );
    },
    getEmptyKms: (state) => {
      const emptyKmPercentage = state.savings_dashboard_info.percentage_empty_km;
      return Math.round(emptyKmPercentage < 1 ? emptyKmPercentage * 100 : emptyKmPercentage);
    },
    getSavingsPercentage: (state) => {
      const savingsValue = state.savings_dashboard_info.savings;
      const percentage = savingsValue < 1 ? savingsValue * 100 : savingsValue;
      return percentage.toFixed(2);
    },
    getMeanSupplierDistance: (state) => {
      return state.suppliers_dashboad_info.mean_supplier_distance || 0;
    },
    getMeanSupplierOccupationPercentage: (state) => {
      const occupationValue = state.suppliers_dashboad_info.mean_supplier_occupation_percentage;
      if (!occupationValue && occupationValue !== 0) return "0";
      
      const percentage = occupationValue * 100;
      return percentage.toFixed(2);
    },
    getSuppliersOccupation: (state) => {
      if (!state.suppliers_occupation) return [];
      return Object.values(state.suppliers_occupation).flat();
    },
    getUniqueSuppliersOccupation: (state) => {
      if (!state.suppliers_occupation) return [];
      
      // Map to hold unique suppliers by supplier number
      const uniqueSuppliers = new Map();
      
      // Process suppliers and keep only unique entries
      Object.values(state.suppliers_occupation).flat().forEach(supplier => {
        if (!uniqueSuppliers.has(supplier.supplier)) {
          uniqueSuppliers.set(supplier.supplier, {
            supplier: supplier.supplier,
            name: supplier.name,
            area: supplier.area
          });
        }
      });
      
      // Turn Map values into an array and sort by name
      return Array.from(uniqueSuppliers.values()).sort((a, b) => 
        a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })
      );
    },
    getContainers: (state) => {
      return state.containers.sort((a, b) => {
        const nameA = a.name.toUpperCase();
        const nameB = b.name.toUpperCase();

        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      });
    },
    getClients: (state) => {
      return state.clients.filter((client) => client.is_client);
    },
    getUrgentServices: (state) => {
      const selectedManagementAreas = state.selected_management_areas;
      const selectedContainers = state.selected_containers;
      const selectedOrderId = state.selected_order_id;
      return state.urgent_services.orders?.filter((order) => {
          const orderContainerId =
            order.type === "export"
              ? order.export.container_type
              : order.import.container_type;
          const orderManagementAreas =
            order.type === "export"
              ? [order.export.id_management_area]
              : [order.import.id_management_area];

          return (
            (selectedContainers?.length === 0 ||
              selectedContainers?.includes(orderContainerId)) &&
            (selectedOrderId === "" ||
              (order.type === "combined" &&
                (order.import.id === selectedOrderId ||
                  order.export.id === selectedOrderId ||
                  order.import.commercial_number === selectedOrderId ||
                  order.export.commercial_number === selectedOrderId)) ||
              (order.type !== "combined" &&
                (order.id === selectedOrderId ||
                  order[order.type].commercial_number === selectedOrderId))) &&
            (selectedManagementAreas?.length === 0 ||
              orderManagementAreas.some((orderManagementArea) =>
                selectedManagementAreas.includes(orderManagementArea)
              )) &&
            (
              // Show long-term orders when show_long_term_orders is true, without extra-day flag
              (order.long_term_plan && state.show_long_term_orders) ||
              // For normal (non long-term) orders, apply extra-day filter
              (!order.long_term_plan && (!order.flag_extra_day || state.hide_extra_day_orders))
            )
          );
        })
        .map((order) => ({
          ...order,
          checked: order.checked ? order.checked : false,
        }));
    },
    getAllSuppliers: (state, getters) => {
      const result = [];
      const map = new Map();

      for (const item of getters.getSupplierMetrics) {
        if (item.id_supplier !== "ALL" && !map.has(item.id_supplier)) {
          map.set(item.id_supplier, true);
          result.push({ id: item.id_supplier, name: item.supplier_name });
        }
      }

      return result.sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
    },
    getAllPorts: (state) => {
      return state.all_ports.sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
    },
    getAllManagementAreas: (state) => {
      return state.all_management_areas.sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
    },
    getPorts: (state) => {
      return state.ports.filter((port) => port.name);
    },
    getManagementAreas: (state) => {
      return state.management_areas;
    },
    getSelectedManagementAreas: (state) => {
      return state.selected_management_areas;
    },
    getSelectedSuppliers: (state) => {
      return state.selected_suppliers;
    },
    getSelectedContainers: (state) => {
      return state.selected_containers;
    },
    getSelectedOrderId: (state) => {
      return state.selected_order_id;
    },
    getAlarms: (state) => {
      let alarmsOrder = state.alarms
        .map((obj) => ({ ...obj, color: "var(--dark-green)" }))
        .sort(
          (a1, a2) => Date.parse(a2.created_at) - Date.parse(a1.created_at)
        );
      alarmsOrder.forEach((element) => {
        switch (element.criticality) {
          case 1:
            element.color = "var(--dark-red)";
            break;
          case 2:
            element.color = "var(--dark-yellow)";
            break;
          default:
            element.color = "var(--dark-green)";
            break;
        }
      });

      return alarmsOrder;
    },
    getUser: (state) => {
      return state.users.find((user) => user.id == session.getUserId());
    },
    getUsers: (state) => {
      return state.users;
    },
    getCoordinates: (state) => {
      return state.coordinate_changes_formatted
        .map((coordinate) => ({
          ...coordinate,
          checked: coordinate.checked ? coordinate.checked : false
        }))
        .sort((a, b) => a.id_location - b.id_location);
    },
    getReviewChanges: (state) => {
      return state.review_changes_formatted.map((review) => ({
        ...review,
        checked: review.checked ? review.checked : false,
      }));
    },
    getPreferredCarriers: (state) => {
      return state.preferred_carriers;
    },
    getPreferredCarriersLogistical: (state) => {
      return state.preferred_carriers_logistical;
    },
    getDelegations: (state) => {
      return state.delegations;
    },
    getDelegationsPorts: (state) => {
      return state.delegations_ports;
    },
    getTruckPlates: (state) => {
      return state.truck_plates;
    },
    getTruckDriverHolidays: (state) => {
      return state.truck_driver_holidays;
    },
    getSupplierZoneChanges: (state) => {
      return state.supplier_zone_changes;
    },
    getSupplierArea: (state) => {
      return state.supplier_area.map((supplier) => ({
        ...supplier,
        activated: !supplier.deactivated,
      }));
    },
    getAreas: (state) => {
      return state.areas;
    },
    getBreakCombinationReasons: (state) => {
      return state.break_combination_reasons;
    },
    getChangeSupplierReasons: (state) => {
      return state.change_supplier_reasons;
    },
    getChangeCombinationReasons: (state) => {
      return state.change_combination_reasons;
    },
    getPreferredCarrierReasons: (state) => {
      return state.preferred_carrier_motives;
    },
    getSelectedDates: (state) => {
      return state.selected_dates;
    },
    getSelectedPortForHistoricDashboard: (state) => {
      return state.selected_port_for_historic_dashboard;
    },
    getSelectedSupplierForHistoricDashboard: (state) => {
      return state.selected_supplier_for_historic_dashboard;
    },
    getPortMetrics: (state) => {
      return state.port_metrics.map((obj) => ({
        ...obj,
        total_savings_percentage: parseFloat(
          (
            ((obj.km_without_combination - obj.km_with_combination) /
              obj.km_without_combination) *
            100
          ).toFixed(2)
        ),
        total_savings: obj.km_without_combination - obj.km_with_combination,
      }));
    },
    getHistoricPortMetrics: (state, getters) => (portSelected) => {
      const filteredObjects = getters.getPortMetrics.filter(
        (obj) => obj.id_port === portSelected
      );

      const total_combined = Number(filteredObjects.reduce(
        (acc, obj) => acc + obj.total_combined,
        0
      ).toFixed(2));

      const total_exports = Number(filteredObjects.reduce(
        (acc, obj) => acc + obj.total_exports,
        0
      ).toFixed(2));

      const total_imports = Number(filteredObjects.reduce(
        (acc, obj) => acc + obj.total_imports,
        0
      ).toFixed(2));

      const total_savings = Number(filteredObjects.reduce(
        (acc, obj) => acc + obj.total_savings,
        0
      ).toFixed(2));

      const km_without_combination_sum = Number(filteredObjects.reduce(
        (acc, obj) => acc + obj.km_without_combination,
        0
      ).toFixed(2));

      const km_with_combination_sum = Number(filteredObjects.reduce(
        (acc, obj) => acc + obj.km_with_combination,
        0
      ).toFixed(2));

      let totals_empty_km = Number(filteredObjects.reduce(
        (acc, obj) => acc + obj.empty_km,
        0
      ).toFixed(2));

      let totals_empty_km_percentage = Number((
        (totals_empty_km / km_without_combination_sum) * 100
      ).toFixed(2));

      let total_savings_percentage = Number((
        ((km_without_combination_sum - km_with_combination_sum) /
          km_without_combination_sum) *
        100
      ).toFixed(2));

      let total_combined_percentage = Number((
        (total_combined / (total_exports + total_imports)) * 100
      ).toFixed(2));

      total_savings_percentage = isNaN(total_savings_percentage)
        ? 0
        : total_savings_percentage;
      total_combined_percentage = isNaN(total_combined_percentage)
        ? 0
        : total_combined_percentage;
      totals_empty_km_percentage = isNaN(totals_empty_km_percentage)
        ? 0
        : totals_empty_km_percentage;

      const total_kilometers = km_without_combination_sum + km_with_combination_sum;
      const total_orders = total_exports + total_imports;

      return {
        total_combined,
        total_exports,
        total_imports,
        total_savings,
        total_savings_percentage,
        total_combined_percentage,
        totals_empty_km_percentage,
        totals_empty_km,
        total_kilometers,
        total_orders,
      };
},
    getHistoricPortData: (state) => {
      const groupedData = {};
      for (const item of state.port_metrics) {
        const port = item.id_port;

        if (!groupedData[port]) {
          groupedData[port] = {
            id_port: port,
            port_name: item.port_name,
            total_combined: 0,
            total_exports: 0,
            total_imports: 0,
            km_without_combination: 0,
            km_with_combination: 0,
            total_services: 0,
            count: 0,
          };
        }

        groupedData[port].total_combined += item.total_combined;
        groupedData[port].total_exports += item.total_exports;
        groupedData[port].total_imports += item.total_imports;
        groupedData[port].km_without_combination += item.km_without_combination;
        groupedData[port].km_with_combination += item.km_with_combination;
        groupedData[port].total_services += item.total_services;
        groupedData[port].count++;
      }

      return Object.values(groupedData).map((obj) => ({
        ...obj,
        total_savings_percentage: parseFloat(
          (
            ((obj.km_without_combination - obj.km_with_combination) /
              obj.km_without_combination) *
            100
          ).toFixed(2)
        ),
        total_savings: obj.km_without_combination - obj.km_with_combination,
      }));
    },
    getHistoricPortDataByDates: (state, getters) => (portSelected) => {
      return getters.getPortMetrics
        .filter((metric) => metric.id_port === portSelected)
        .map((metric) => {
          if (isNaN(metric.total_savings_percentage)) {
            metric.total_savings_percentage = 0;
          }
          return metric;
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    },
    getCombinedAndSavingPercentagesForPorts: (state, getters) => {
      let totalCombined = 0;
      let totalExportImport = 0;
      let totalKmWithCombination = 0;
      let totalKmWithoutCombination = 0;
      const data = getters.getHistoricPortDataByDates("ESTOT");

      for (let i = 0; i < data.length; i++) {
        totalCombined += data[i].total_combined;
        totalExportImport += data[i].total_exports + data[i].total_imports;
        totalKmWithCombination += data[i].km_with_combination;
        totalKmWithoutCombination += data[i].km_without_combination;
      }

      let percentageCombinations = (totalCombined / totalExportImport) * 100;
      let percentageSavings =
        ((totalKmWithoutCombination - totalKmWithCombination) /
          totalKmWithoutCombination) *
        100;

      percentageCombinations = isNaN(percentageCombinations)
        ? 0
        : Math.round(percentageCombinations);
      percentageSavings = isNaN(percentageSavings)
        ? 0
        : Math.round(percentageSavings);
      return [percentageCombinations, percentageSavings];
    },
    getSupplierMetrics: (state) => {
      return state.supplier_metrics.map((obj) => ({
        ...obj,
        total_savings_percentage: parseFloat(
          (
            ((obj.km_without_combination - obj.km_with_combination) /
              obj.km_without_combination) *
            100
          ).toFixed(2)
        ),
        total_savings: obj.km_without_combination - obj.km_with_combination,
      }));
    },
    getHistoricSupplierMetrics: (state, getters) => (supplierSelected) => {
      const filteredObjects = getters.getSupplierMetrics.filter(
        (obj) => obj.id_supplier === supplierSelected
      );

      const total_combined = Number(filteredObjects.reduce(
        (acc, obj) => acc + obj.total_combined,
        0
      ).toFixed(2));

      const total_exports = Number(filteredObjects.reduce(
        (acc, obj) => acc + obj.total_exports,
        0
      ).toFixed(2));

      const total_imports = Number(filteredObjects.reduce(
        (acc, obj) => acc + obj.total_imports,
        0
      ).toFixed(2));

      const total_savings = Number(filteredObjects.reduce(
        (acc, obj) => acc + obj.total_savings,
        0
      ).toFixed(2));

      const total_late = Number(filteredObjects.reduce(
        (acc, obj) => acc + obj.late,
        0
      ).toFixed(2));

      const km_without_combination_sum = Number(filteredObjects.reduce(
        (acc, obj) => acc + obj.km_without_combination,
        0
      ).toFixed(2));

      const km_with_combination_sum = Number(filteredObjects.reduce(
        (acc, obj) => acc + obj.km_with_combination,
        0
      ).toFixed(2));

      let totals_empty_km = Number(filteredObjects.reduce(
        (acc, obj) => acc + obj.empty_km,
        0
      ).toFixed(2));

      let totals_empty_km_percentage = Number((
        (totals_empty_km / km_with_combination_sum) * 100
      ).toFixed(2));

      let total_savings_percentage = Number((
        ((km_without_combination_sum - km_with_combination_sum) /
          km_without_combination_sum) *
        100
      ).toFixed(2));

      let total_combined_percentage = Number((
        (total_combined / (total_exports + total_imports)) * 100
      ).toFixed(2));

      let total_kpi = Number((
        ((total_exports + total_imports - total_late) /
          (total_exports + total_imports)) *
        100
      ).toFixed(2));

      let total_occupation = Number((filteredObjects.reduce(
        (acc, obj) => acc + obj.occupation,
        0
      ) / filteredObjects.length * 100).toFixed(2));

      total_savings_percentage = isNaN(total_savings_percentage)
        ? 0
        : total_savings_percentage;
      total_combined_percentage = isNaN(total_combined_percentage)
        ? 0
        : total_combined_percentage;
      totals_empty_km_percentage = isNaN(totals_empty_km_percentage)
        ? 0
        : totals_empty_km_percentage;
      total_kpi = isNaN(total_kpi) ? 0 : Math.round(total_kpi);
      total_occupation = isNaN(total_occupation) ? 0 : Math.round(total_occupation);
 
      const total_kilometers = km_without_combination_sum + km_with_combination_sum;
      const total_orders = total_exports + total_imports;

      return {
        total_combined,
        total_exports,
        total_imports,
        total_savings,
        total_savings_percentage,
        total_combined_percentage,
        total_kpi,
        total_occupation,
        totals_empty_km_percentage,
        totals_empty_km,
        total_kilometers,
        total_orders
      };
    },
    getHistoricSupplierDataByDates: (state, getters) => (supplierSelected) => {
      return getters.getSupplierMetrics
        .filter((metric) => metric.id_supplier === supplierSelected)
        .map((metric) => {
          metric.average_occupation = Math.round(metric.occupation * 100);
          metric.average_kpi = Math.round(metric.kpi * 100);
          if (isNaN(metric.total_savings_percentage)) {
            metric.total_savings_percentage = 0;
          }
          if (isNaN(metric.average_kpi)) {
            metric.average_kpi = 0;
          }
          if (isNaN(metric.average_occupation)) {
            metric.average_occupation = 0;
          }
          return metric;
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    },
    getHistoricSupplierData: (state) => {
      const groupedData = {};
      for (const item of state.supplier_metrics) {
        const supplier = item.id_supplier;

        if (!groupedData[supplier]) {
          groupedData[supplier] = {
            id_supplier: supplier,
            supplier_name: item.supplier_name,
            fleet: item.fleet,
            km_without_combination: 0,
            km_with_combination: 0,
            total_services: 0,
            count: 0,
            occupation_sum: 0,
            kpi_sum: 0,
          };
        }

        groupedData[supplier].km_without_combination +=
          item.km_without_combination;
        groupedData[supplier].km_with_combination += item.km_with_combination;
        groupedData[supplier].total_services += item.total_services;
        groupedData[supplier].count++;
        groupedData[supplier].occupation_sum += item.occupation;
        groupedData[supplier].kpi_sum += item.kpi;
      }

      return Object.values(groupedData).map((obj) => ({
        ...obj,
        total_savings_percentage: parseFloat(
          (
            ((obj.km_without_combination - obj.km_with_combination) /
              obj.km_without_combination) *
            100
          ).toFixed(2)
        ),
        average_occupation: Math.round((obj.occupation_sum / obj.count) * 100),
        average_kpi: Math.round((obj.kpi_sum / obj.count) * 100),
      }));
    },
    getShowOnlyExtraDayOrders: (state) => {
      return state.hide_extra_day_orders;
    },
    isAppLoading: (state) => {
      return state.isLoading || state.loadingOperations > 0;
    },
    getShowLongTermOrders: (state) => {
      return state.show_long_term_orders;
    },
    getLongTermOrders: (state) => state.long_term_orders,
    getOrderManager: (state) => {
      return state.orderManager;
    },
    getSupplierAvailabilityMotives: (state) => state.supplier_availability_motives,
    // Access control getters: assume access unless explicitly denied
    // null = not checked yet (show), true = allowed (show), false = denied (hide)
    hasAccessToPreferredCarriers: (state) => state.access_control.preferred_carriers !== false,
    hasAccessToUsers: (state) => state.access_control.users !== false,
    hasAccessToDelegations: (state) => state.access_control.delegations !== false,
    hasAccessToDelegationsPorts: (state) => state.access_control.delegations_ports !== false,
    hasAccessToTruckPlates: (state) => state.access_control.truck_plates !== false,
    hasAccessToTruckDriverHolidays: (state) => state.access_control.truck_driver_holidays !== false,
    hasAccessToSupplierZoneChanges: (state) =>
      state.access_control.supplier_zone_changes !== false,
    hasAccessToCoordinates: (state) => state.access_control.coordinates !== false,
    hasAccessToSupplierInfo: (state) => state.access_control.supplier_info !== false,
    hasAccessToAreas: (state) => state.access_control.areas !== false,
    hasAccessToReviewChanges: (state) => state.access_control.review_changes !== false,
    hasAccessToPreferredCarrierMotives: (state) => state.access_control.preferred_carrier_motives !== false,
    hasAccessToSupplierAvailabilityMotives: (state) => state.access_control.supplier_availability_motives !== false,
    // Combined access check for ManagePreferredCarriersTab
    hasAccessToManagePreferredCarriers: (state) => 
      state.access_control.preferred_carriers !== false || state.access_control.preferred_carriers_logistical !== false,
    hasAccessToPreferredCarriersProvider: (state) => 
      state.access_control.preferred_carriers !== false,
    hasAccessToPreferredCarriersLogistical: (state) => 
      state.access_control.preferred_carriers_logistical !== false,
    // Whether access checks finished for admin features
    isAccessControlLoadedForAdmin: (state) => 
      state.access_control.preferred_carriers !== null &&
      state.access_control.preferred_carriers_logistical !== null &&
      state.access_control.users !== null &&
      state.access_control.delegations !== null &&
      state.access_control.truck_plates !== null &&
      state.access_control.supplier_info !== null,
    // Whether access checks finished for supervisor features
    isAccessControlLoadedForSupervisor: (state) =>
      state.access_control.review_changes !== null &&
      state.access_control.coordinates !== null,
  },
  actions: {
    INITIALIZE_STORE({ getters, commit, dispatch, state }) {
      const loggedIn = session.isAuthenticated();
      if (loggedIn) {
        commit("SET_LOADING", true);
        commit("SET_ROLE", session.getRole());
        
        // Default profile mode from available roles
        // Priority: DISPATCHER > SUPERVISOR > ADMIN > HISTORIC
        const userRoles = session.getRole();
        let defaultProfileMode = "";
        if (userRoles.includes(1)) {
          defaultProfileMode = "DISPATCHER";
        } else if (userRoles.includes(2)) {
          defaultProfileMode = "SUPERVISOR";
        } else if (userRoles.includes(3)) {
          defaultProfileMode = "ADMIN";
        } else if (userRoles.includes(4)) {
          defaultProfileMode = "HISTORIC";
        } else {
          defaultProfileMode = getters.getRole; // fallback
        }
        
        commit("SET_PROFILE_MODE", defaultProfileMode);
        
        // Helper to handle API errors
        const safeDispatch = createSafeDispatch(commit, dispatch);
        
        let dataPromise;
        
        switch (getters.getProfileMode) {
          case "DISPATCHER":
            // Load data first and wait for completion
            dataPromise = safeDispatch("LOAD_DATA");
            
            // Set up recursive loading after initial load finishes
            dataPromise
              .then(() => {
                // Only set up recursive loading once initial data is loaded
                dispatch("LOAD_DATA_RECURSIVELY");
                dispatch("SCHEDULE_HOURLY_LOAD_DATA");
              })
              .catch(error => {
                console.error("Error setting up recursive loading for DISPATCHER:", error);
                // Still try to set up recursive loading
                dispatch("LOAD_DATA_RECURSIVELY");
                dispatch("SCHEDULE_HOURLY_LOAD_DATA");
              });
            break;
          case "SUPERVISOR":
            // Load data first and wait for completion
            dataPromise = safeDispatch("LOAD_SUPERVISOR_DATA");
            
            // Set up recursive loading after initial load finishes
            dataPromise
              .then(() => {
                // Only set up recursive loading once initial data is loaded
                dispatch("LOAD_SUPERVISOR_DATA_RECURSIVELY");
                dispatch("SCHEDULE_HOURLY_LOAD_SUPERVISOR_DATA");
              })
              .catch(error => {
                console.error("Error setting up recursive loading for SUPERVISOR:", error);
                // Still try to set up recursive loading
                dispatch("LOAD_SUPERVISOR_DATA_RECURSIVELY");
                dispatch("SCHEDULE_HOURLY_LOAD_SUPERVISOR_DATA");
              });
            break;
          case "ADMIN":
            // Load data first and wait for completion
            dataPromise = safeDispatch("LOAD_ADMIN_DATA");
            
            // Set up recursive loading after initial load finishes
            dataPromise
              .then(() => {
                // Only set up recursive loading once initial data is loaded
                dispatch("LOAD_ADMIN_DATA_RECURSIVELY");
              })
              .catch(error => {
                console.error("Error setting up recursive loading for ADMIN:", error);
                // Still try to set up recursive loading
                dispatch("LOAD_ADMIN_DATA_RECURSIVELY");
              });
            break;
          case "HISTORIC":
            // Load admin and historic data in parallel
            dataPromise = Promise.all([
              safeDispatch("LOAD_ADMIN_DATA"),
              safeDispatch("LOAD_HISTORIC_DATA")
            ]);
            
            // Set up recursive loading after initial load finishes
            dataPromise
              .then(() => {
                // Only set up recursive loading once initial data is loaded
                dispatch("LOAD_ADMIN_DATA_RECURSIVELY");
              })
              .catch(error => {
                console.error("Error setting up recursive loading for HISTORIC:", error);
                // Still try to set up recursive loading
                dispatch("LOAD_ADMIN_DATA_RECURSIVELY");
              });
            break;
          default:
            dataPromise = Promise.resolve();
        }
        
        // Timeout so tabs appear even if some APIs fail or are slow
        setTimeout(() => {
          // Default access for endpoints not yet verified
          Object.keys(state.access_control).forEach(endpoint => {
            if (state.access_control[endpoint] === null) {
              commit("SET_ACCESS_CONTROL", { endpoint, hasAccess: true });
            }
          });
          // Mark access verification complete
          commit("SET_ACCESS_CONTROL_VERIFIED");
        }, 5000); // 5 second timeout
        
        return dataPromise.finally(() => {
          commit("SET_LOADING", false);
          // Mark access verification complete after all data loading attempts
          commit("SET_ACCESS_CONTROL_VERIFIED");
        });
      }
      
      return Promise.resolve();
    },
    LOAD_DATA_RECURSIVELY({ dispatch, commit, state }) {
      // Clear any existing interval to avoid duplicate requests
      if (state.data_interval) {
        clearInterval(state.data_interval);
      }

      // No immediate dispatch: data was already loaded in INITIALIZE_STORE
      // Avoids duplicating the initial request
      
      // Schedule interval for future data loads
      const intervalId = setInterval(
        () => dispatch("LOAD_DATA"),
        15 * 60 * 1000  // Every 15 minutes
      );
      commit("SET_DATA_INTERVAL", intervalId);
    },
    LOAD_SUPERVISOR_DATA_RECURSIVELY({ dispatch, commit, state }) {
      // Clear any existing interval to avoid duplicate requests
      if (state.data_interval) {
        clearInterval(state.data_interval);
      }

      // No immediate dispatch: data was already loaded in INITIALIZE_STORE
      // Avoids duplicating the initial request
      
      // Schedule interval for future data loads
      const intervalId = setInterval(
        () => dispatch("LOAD_SUPERVISOR_DATA"),
        15 * 60 * 1000  // Every 15 minutes
      );
      commit("SET_DATA_INTERVAL", intervalId);
    },
    LOAD_ADMIN_DATA_RECURSIVELY({ dispatch, commit, state }) {
      // Clear any existing interval to avoid duplicate requests
      if (state.data_interval) {
        clearInterval(state.data_interval);
      }

      // No immediate dispatch: data was already loaded in INITIALIZE_STORE
      // Avoids duplicating the initial request
      
      // Schedule interval for future data loads
      const intervalId = setInterval(
        () => dispatch("LOAD_ADMIN_DATA"),
        15 * 60 * 1000  // Every 15 minutes
      );
      commit("SET_DATA_INTERVAL", intervalId);
    },
    SCHEDULE_HOURLY_LOAD_DATA({ dispatch, commit, state }) {
      const currentDate = new Date();
      const currentMinute = currentDate.getMinutes();

      // Minutes until next hour plus 5 minutes
      const minutesUntilNextHour = (60 - currentMinute) % 60;
      const scheduledMinute = (currentMinute + minutesUntilNextHour + 5) % 60;

      // Milliseconds until the next scheduled minute
      const millisecondsUntilScheduledMinute =
        (scheduledMinute - currentMinute) * 60 * 1000;

      setTimeout(() => {
        if (state.hour_data_interval) {
          clearInterval(state.hour_data_interval);
        }

        const intervalId = setInterval(
          () => dispatch("LOAD_DATA"),
          60 * 60 * 1000
        );
        commit("SET_HOUR_DATA_INTERVAL", intervalId);
      }, millisecondsUntilScheduledMinute);
    },
    SCHEDULE_HOURLY_LOAD_SUPERVISOR_DATA({ dispatch, commit, state }) {
      const currentDate = new Date();
      const currentMinute = currentDate.getMinutes();

      // Minutes until next hour plus 5 minutes
      const minutesUntilNextHour = (60 - currentMinute) % 60;
      const scheduledMinute = (currentMinute + minutesUntilNextHour + 5) % 60;

      // Milliseconds until the next scheduled minute
      const millisecondsUntilScheduledMinute =
        (scheduledMinute - currentMinute) * 60 * 1000;

      setTimeout(() => {
        if (state.hour_data_interval) {
          clearInterval(state.hour_data_interval);
        }

        const intervalId = setInterval(
          () => dispatch("LOAD_SUPERVISOR_DATA"),
          60 * 60 * 1000
        );
        commit("SET_HOUR_DATA_INTERVAL", intervalId);
      }, millisecondsUntilScheduledMinute);
    },
    LOAD_DATA({ dispatch, commit }) {
      commit("INCREMENT_LOADING_OPERATIONS");
      
      // Helper to handle API errors
      const safeDispatch = createSafeDispatch(commit, dispatch);
      
      const promises = [
        safeDispatch("LOAD_METRICS"),
        safeDispatch("LOAD_ALARMS"),
        safeDispatch("LOAD_URGENT_SERVICES"),
        safeDispatch("LOAD_LAST_RESOLUTION"),
        safeDispatch("LOAD_MANAGEMENT_AREAS"),
        safeDispatch("LOAD_CONTAINERS"),
        safeDispatch("LOAD_CHANGE_SUPPLIER_REASONS"),
        safeDispatch("LOAD_BREAK_COMBINATION_REASONS"),
        safeDispatch("LOAD_CHANGE_COMBINATION_REASONS"),
        safeDispatch("LOAD_TRUCK_PLATES"),
        safeDispatch("LOAD_TRUCK_DRIVER_HOLIDAYS"),
        safeDispatch("LOAD_SUPPLIER_ZONE_CHANGES"),
        safeDispatch("LOAD_SUPPLIER_INFO"),
      ];
      
      return Promise.all(promises).finally(() => {
        commit("DECREMENT_LOADING_OPERATIONS");
      });
    },
    LOAD_SUPERVISOR_DATA({ dispatch, commit }) {
      commit("INCREMENT_LOADING_OPERATIONS");
      
      // Helper to handle API errors
      const safeDispatch = createSafeDispatch(commit, dispatch);
      
      const promises = [
        safeDispatch("LOAD_LAST_RESOLUTION"), 
        safeDispatch("LOAD_ORDER_DASHBOARD_INFO"),
        safeDispatch("LOAD_METRICS"),
        safeDispatch("LOAD_ALARMS"),
        safeDispatch("LOAD_MANAGEMENT_AREAS"),
        safeDispatch("LOAD_CONTAINERS"),
        safeDispatch("LOAD_CHANGE_SUPPLIER_REASONS"),
        safeDispatch("LOAD_BREAK_COMBINATION_REASONS"),
        safeDispatch("LOAD_CHANGE_COMBINATION_REASONS"),
        safeDispatch("LOAD_REVIEW_CHANGES"),
        safeDispatch("LOAD_COORDINATES")
      ];
      
      return Promise.all(promises).finally(() => {
        commit("DECREMENT_LOADING_OPERATIONS");
      });
    },
    LOAD_ADMIN_DATA({ dispatch, commit }) {
      commit("INCREMENT_LOADING_OPERATIONS");
      
      // Helper to handle API errors
      const safeDispatch = createSafeDispatch(commit, dispatch);
      
      const promises = [
        safeDispatch("LOAD_ORDER_DASHBOARD_INFO"),
        safeDispatch("LOAD_METRICS"),
        safeDispatch("LOAD_ALARMS"),
        safeDispatch("LOAD_MANAGEMENT_AREAS"),
        safeDispatch("LOAD_CONTAINERS"),
        safeDispatch("LOAD_USERS"),
        safeDispatch("LOAD_CHANGE_SUPPLIER_REASONS"),
        safeDispatch("LOAD_BREAK_COMBINATION_REASONS"),
        safeDispatch("LOAD_CHANGE_COMBINATION_REASONS"),
        safeDispatch("LOAD_PREFERRED_CARRIER_MOTIVES"),
        safeDispatch("LOAD_PREFERRED_CARRIERS"),
        safeDispatch("LOAD_PREFERRED_CARRIERS_LOGISTICAL"),
        safeDispatch("LOAD_DELEGATIONS"),
        safeDispatch("LOAD_DELEGATIONS_PORTS"),
        safeDispatch("LOAD_TRUCK_PLATES"),
        safeDispatch("LOAD_TRUCK_DRIVER_HOLIDAYS"),
        safeDispatch("LOAD_ALL_PORTS"),
        safeDispatch("LOAD_ALL_MANAGEMENT_AREAS"),
        safeDispatch("LOAD_AREAS"),
        safeDispatch("LOAD_SUPPLIER_INFO")
      ];
      
      return Promise.all(promises).finally(() => {
        commit("DECREMENT_LOADING_OPERATIONS");
      });
    },
    LOAD_HISTORIC_DATA() {
      return Promise.resolve();
    },
    LOAD_LAST_RESOLUTION({ commit, dispatch }) {
      return client.get(`/external/solution/data/`).then((response) => {
        if (response.status === 200) {
          commit("SET_LAST_RESOLUTION", response.content);
          return dispatch("LOAD_LAST_RESOLUTION_TIME");
        }
        return Promise.resolve();
      });
    },
    LOAD_LAST_RESOLUTION_TIME({ commit }) {
      return client.get(`/external/solution/time/`).then((response) => {
        if (response.status === 200) {
          commit("SET_LAST_RESOLUTION_TIME", response.content);
        }
        return Promise.resolve();
      });
    },
    LOAD_MANAGEMENT_AREAS({ commit }) {
      return client
        .get(`/external/user/${session.getUserId()}/management/areas/`)
        .then((response) => {
          if (response.status === 200) {
            commit("SET_MANAGEMENT_AREAS", response.content.management_areas);
            commit("SET_PORTS", response.content.ports);
            commit("SET_USER", {
              id: response.content.user_id,
              role: response.content.role_id || response.content.roles || [],
            });
          } else if (response.status === 401) {
            if (session.signOut() === true) {
              localStorage.removeItem("username");
              localStorage.removeItem("password");
              this.$router.push("/sign-in");
            }
          }
          return Promise.resolve();
        });
    },
    LOAD_COORDINATES({ commit }) {
      return client.get(`/external/locations/coordinates/`).then((response) => {
        if (response.status === 200) {
          commit("SET_COORDINATES", response.content);
          commit("SET_ACCESS_CONTROL", { endpoint: "coordinates", hasAccess: true });
        } else if (response.status === 403) {
          commit("SET_ACCESS_CONTROL", { endpoint: "coordinates", hasAccess: false });
          console.warn("Access denied to coordinates endpoint");
        }
        return Promise.resolve();
      }).catch(error => {
        console.error("Error loading coordinates:", error);
        return Promise.resolve();
      });
    },
    LOAD_DELEGATIONS({ commit }) {
      return client.get(`/external/delegations/`).then((response) => {
        if (response.status === 200) {
          commit("SET_DELEGATIONS", response.content);
          commit("SET_ACCESS_CONTROL", { endpoint: "delegations", hasAccess: true });
        } else if (response.status === 403) {
          commit("SET_ACCESS_CONTROL", { endpoint: "delegations", hasAccess: false });
          console.warn("Access denied to delegations endpoint");
        }
        return Promise.resolve();
      }).catch(error => {
        console.error("Error loading delegations:", error);
        return Promise.resolve();
      });
    },
    LOAD_DELEGATIONS_PORTS({ commit }) {
      return client.get(`/external/delegations/ports/`).then((response) => {
        if (response.status === 200) {
          commit("SET_DELEGATIONS_PORTS", response.content);
          commit("SET_ACCESS_CONTROL", { endpoint: "delegations_ports", hasAccess: true });
        } else if (response.status === 403) {
          commit("SET_ACCESS_CONTROL", { endpoint: "delegations_ports", hasAccess: false });
          console.warn("Access denied to delegations ports endpoint");
        }
        return Promise.resolve();
      }).catch(error => {
        console.error("Error loading delegations ports:", error);
        return Promise.resolve();
      });
    },
    LOAD_TRUCK_PLATES({ commit }) {
      return client.get(`/external/truck/plates/`).then((response) => {
        if (response.status === 200) {
          commit("SET_TRUCK_PLATES", response.content);
          commit("SET_ACCESS_CONTROL", { endpoint: "truck_plates", hasAccess: true });
        } else if (response.status === 403) {
          commit("SET_ACCESS_CONTROL", { endpoint: "truck_plates", hasAccess: false });
          console.warn("Access denied to truck plates endpoint");
        }
        return Promise.resolve();
      }).catch(error => {
        console.error("Error loading truck plates:", error);
        return Promise.resolve();
      });
    },
    LOAD_TRUCK_DRIVER_HOLIDAYS({ commit }) {
      return client.get(`/external/truck/driver/holidays/`).then((response) => {
        if (response.status === 200) {
          commit("SET_TRUCK_DRIVER_HOLIDAYS", response.content);
          commit("SET_ACCESS_CONTROL", { endpoint: "truck_driver_holidays", hasAccess: true });
        } else if (response.status === 403) {
          commit("SET_ACCESS_CONTROL", { endpoint: "truck_driver_holidays", hasAccess: false });
          console.warn("Access denied to truck driver holidays endpoint");
        }
        return Promise.resolve();
      }).catch(error => {
        console.error("Error loading truck driver holidays:", error);
        return Promise.resolve();
      });
    },
    LOAD_SUPPLIER_ZONE_CHANGES({ commit }) {
      const today = moment().format("YYYY-MM-DD");
      return client.get(`/external/supplier/zone/changes/`, { date: today }).then((response) => {
        const status = Number(response.status);
        if (status === 200) {
          commit("SET_SUPPLIER_ZONE_CHANGES", response.content);
          commit("SET_ACCESS_CONTROL", {
            endpoint: "supplier_zone_changes",
            hasAccess: true,
            force: true,
          });
        } else if (status === 400 || status === 403) {
          commit("SET_SUPPLIER_ZONE_CHANGES", []);
          commit("SET_ACCESS_CONTROL", {
            endpoint: "supplier_zone_changes",
            hasAccess: false,
            force: true,
          });
          console.warn(
            status === 400
              ? "No south delegation ports: supplier zone changes unavailable"
              : "Access denied to supplier zone changes endpoint"
          );
        }
        return Promise.resolve();
      }).catch(error => {
        console.error("Error loading supplier zone changes:", error);
        return Promise.resolve();
      });
    },
    async CREATE_SUPPLIER_ZONE_CHANGE({ dispatch }, { data }) {
      try {
        const payload = {
          zone_change: Number(data.zone_change) || 0,
          zone_change_reefer: Number(data.zone_change_reefer) || 0,
          zone: data.zone || "",
          zone_change_tilt20: Number(data.zone_change_tilt20) || 0,
          zone_change_tilt40: Number(data.zone_change_tilt40) || 0,
          id_supplier: Number(data.id_supplier) || 0,
          zone_change_dangerous_cargo: Number(data.zone_change_dangerous_cargo) || 0,
          date: data.date || moment().format("YYYY-MM-DD"),
        };
        const { status } = await client.post(
          `/external/supplier/zone/changes/`,
          payload,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        if (Number(status) === 201) {
          await dispatch("LOAD_SUPPLIER_ZONE_CHANGES");
          return { success: true };
        }
        return { success: false };
      } catch (error) {
        console.error("Error creating supplier zone change:", error);
        return { success: false };
      }
    },
    async UPDATE_SUPPLIER_ZONE_CHANGE({ dispatch }, { id, data }) {
      try {
        const payload = {
          zone_change: Number(data.zone_change) || 0,
          zone_change_reefer: Number(data.zone_change_reefer) || 0,
          zone: data.zone || "",
          zone_change_tilt20: Number(data.zone_change_tilt20) || 0,
          zone_change_tilt40: Number(data.zone_change_tilt40) || 0,
          id_supplier: Number(data.id_supplier) || 0,
          zone_change_dangerous_cargo: Number(data.zone_change_dangerous_cargo) || 0,
          date: data.date || moment().format("YYYY-MM-DD"),
        };
        const { status } = await client.put(
          `/external/supplier/zone/changes/${id}/`,
          payload,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        if (Number(status) === 200) {
          await dispatch("LOAD_SUPPLIER_ZONE_CHANGES");
          return { success: true };
        }
        return { success: false };
      } catch (error) {
        console.error("Error updating supplier zone change:", error);
        return { success: false };
      }
    },
    LOAD_ALL_PORTS({ commit }) {
      return client.get(`/external/ports/`).then((response) => {
        if (response.status === 200) {
          commit("SET_ALL_PORTS", response.content);
        }
        return Promise.resolve();
      });
    },
    LOAD_ALL_MANAGEMENT_AREAS({ commit }) {
      return client.get(`/external/management/areas/`).then((response) => {
        if (response.status === 200) {
          commit("SET_ALL_MANAGEMENT_AREAS", response.content);
        }
        return Promise.resolve();
      });
    },
    LOAD_CONTAINERS({ commit }) {
      return client.get(`/external/containers/`).then((response) => {
        if (response.status === 200) {
          commit("SET_CONTAINERS", response.content);
        }
        return Promise.resolve();
      });
    },
    LOAD_CLIENTS({ commit }) {
      return client.get(`/external/locations/`).then((response) => {
        if (response.status === 200) {
          commit("SET_CLIENTS", response.content);
        }
        return Promise.resolve();
      });
    },
    LOAD_USERS({ commit }) {
      return client.get(`/external/users/`).then((response) => {
        if (response.status === 200) {
          commit("SET_USERS", response.content);
          commit("SET_ACCESS_CONTROL", { endpoint: "users", hasAccess: true });
        } else if (response.status === 403) {
          commit("SET_ACCESS_CONTROL", { endpoint: "users", hasAccess: false });
          console.warn("Access denied to users endpoint");
        }
        return Promise.resolve();
      }).catch(error => {
        console.error("Error loading users:", error);
        commit("SET_ACCESS_CONTROL", { endpoint: "users", hasAccess: false });
        return Promise.resolve();
      });
    },
    LOAD_ALARMS({ commit }) {
      return client.get(`/external/alarms/user/`).then((response) => {
        if (response.status === 200) {
          commit("SET_ALARMS", response.content);
        }
        return Promise.resolve();
      });
    },
    LOAD_ORDER_DASHBOARD_INFO({ commit }) {
      return client.get(`/external/supervisor/kpi/`).then((response) => {
        if (response.status === 200) {
          commit("SET_ORDER_DASHBOARD_INFO", response.content);
        }
        return Promise.resolve();
      });
    },
    LOAD_SAVING_DASHBOARD_INFO({ commit, state }) {
      let url = "";
      if (state.selected_management_areas.length > 0) {
        const areas = state.selected_management_areas.map(
          (area) => `areas=${area}`
        );
        url += `?${areas.join("&")}`;
      }

      const request1 = client.get(`/external/solution/kpi/`, url)
        .catch(error => {
          console.error("Error loading solution KPI:", error);
          return { status: 0, content: {} };
        });
        
      const request2 = client.get(`/external/empty/km/`, url)
        .catch(error => {
          console.error("Error loading empty km:", error);
          return { status: 0, content: {} };
        });

      return Promise.all([request1, request2])
        .then((responses) => {
          const response1 = responses[0];
          const response2 = responses[1];

          if (response1.status === 200 && response2.status === 200) {
            const combinedContent = {
              ...response1.content,
              ...response2.content,
            };
            commit("SET_SAVING_DASHBOARD_INFO", combinedContent);
          }
          return Promise.resolve();
        })
        .catch(error => {
          console.error("Error in Promise.all for LOAD_SAVING_DASHBOARD_INFO:", error);
          return Promise.resolve();
        });
    },
    LOAD_SUPPLIER_DASHBOARD_INFO({ commit, state }) {
      let url = "";
      if (state.selected_management_areas.length > 0) {
        const areas = state.selected_management_areas.map(
          (area) => `areas=${area}`
        );
        url += `?${areas.join("&")}`;
      }

      return client.get(`/external/supplier/info/`, url)
        .then((response) => {
          if (response.status === 200) {
            commit("SET_SUPPLIER_DASHBOARD_INFO", response.content);
          }
          return Promise.resolve();
        })
        .catch((error) => {
          console.error("Error loading supplier dashboard info:", error);
          return Promise.resolve(); // Still resolve so callers do not hang on error
        });
    },

    LOAD_URGENT_SERVICES({ commit }) {
      return client.get(`/external/services/urgent/`).then((response) => {
        if (response.status === 200) {
          commit("SET_URGENT_SERVICES", response.content);
        }
        return Promise.resolve();
      });
    },
    LOAD_CHANGE_SUPPLIER_REASONS({ commit }) {
      return client.get(`/external/motives/supplier/`).then((response) => {
        if (response.status === 200) {
          commit("SET_CHANGE_SUPPLIER_MOTIVES", response.content);
        }
        return Promise.resolve();
      });
    },
    LOAD_BREAK_COMBINATION_REASONS({ commit }) {
      return client.get(`/external/motives/break/`).then((response) => {
        if (response.status === 200) {
          commit("SET_BREAK_COMBINATION_MOTIVES", response.content);
        }
        return Promise.resolve();
      });
    },
    LOAD_CHANGE_COMBINATION_REASONS({ commit }) {
      return client.get(`/external/motives/routes/`).then((response) => {
        if (response.status === 200) {
          commit("SET_CHANGE_COMBINATION_MOTIVES", response.content);
        }
        return Promise.resolve();
      });
    },
    LOAD_PREFERRED_CARRIER_MOTIVES({ commit }) {
      return client.get(`/external/preferred/carriers/motives/`).then((response) => {
        if (response.status === 200) {
          commit("SET_PREFERRED_CARRIER_MOTIVES", response.content);
          commit("SET_ACCESS_CONTROL", { endpoint: "preferred_carrier_motives", hasAccess: true });
        } else if (response.status === 403) {
          commit("SET_ACCESS_CONTROL", { endpoint: "preferred_carrier_motives", hasAccess: false });
          console.warn("Access denied to preferred carrier motives endpoint");
        }
        return Promise.resolve();
      }).catch(error => {
        console.error("Error loading preferred carrier motives:", error);
        return Promise.resolve();
      });
    },
    LOAD_REVIEW_CHANGES({ commit }) {
      return client.get(`/external/changes/review/`).then((response) => {
        if (response.status === 200) {
          commit("SET_REVIEW_CHANGES", response.content);
          commit("SET_ACCESS_CONTROL", { endpoint: "review_changes", hasAccess: true });
        } else if (response.status === 403) {
          commit("SET_ACCESS_CONTROL", { endpoint: "review_changes", hasAccess: false });
          console.warn("Access denied to review changes endpoint");
        }
        return Promise.resolve();
      }).catch(error => {
        console.error("Error loading review changes:", error);
        return Promise.resolve();
      });
    },
    LOAD_SUPPLIER_INFO({ commit }) {
      return client.get(`/external/suppliers/`).then((response) => {
        if (response.status === 200) {
          commit("SET_SUPPLIER_INFO", response.content);
          commit("SET_ACCESS_CONTROL", { endpoint: "supplier_info", hasAccess: true });
        } else if (response.status === 403) {
          commit("SET_ACCESS_CONTROL", { endpoint: "supplier_info", hasAccess: false });
          console.warn("Access denied to supplier info endpoint");
        }
        return Promise.resolve();
      }).catch(error => {
        console.error("Error loading supplier info:", error);
        return Promise.resolve();
      });
    },
    async CREATE_SUPPLIER({ dispatch }, { data }) {
      try {
        const payload = {
          id: Number(data.id),
          area: data.area || "",
          name: data.name || "",
          kpi: Number(data.kpi) || 0,
        };
        const { status, content } = await client.post(
          `/external/suppliers/`,
          payload,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        const statusCode = Number(status);
        if (statusCode === 201) {
          await dispatch("LOAD_SUPPLIER_INFO");
          return { success: true };
        }

        const rawError = content?.message || content?.error || "";
        const looksLikeDuplicate =
          statusCode === 409 ||
          /integrity|duplicate|already exists|unique|constraint/i.test(String(rawError));

        return {
          success: false,
          status: statusCode,
          error: looksLikeDuplicate ? "El proveedor ya existe" : (rawError || "No se pudo crear el proveedor"),
          rawError,
        };
      } catch (error) {
        console.error("Error creating supplier:", error);
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "No se pudo crear el proveedor";
        const looksLikeDuplicate = /integrity|duplicate|already exists|unique|constraint/i.test(String(errorMessage));
        return { success: false, status: 0, error: looksLikeDuplicate ? "El proveedor ya existe" : errorMessage };
      }
    },
    async UPDATE_SUPPLIER({ dispatch }, { data }) {
      try {
        const id = Number(data.id);
        const payload = {
          id,
          area: data.area != null && data.area !== "" ? String(data.area) : "",
          name: String(data.name || "").trim(),
          kpi: data.kpi != null && data.kpi !== "" ? Number(data.kpi) : 0,
        };
        const { status } = await client.put(
          `/external/suppliers/${id}/`,
          payload,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        const statusCode = Number(status);
        if (statusCode === 200 || statusCode === 204) {
          await dispatch("LOAD_SUPPLIER_INFO");
          return { success: true };
        }
        return { success: false };
      } catch (error) {
        console.error("Error updating supplier:", error);
        return { success: false };
      }
    },
    LOAD_AREAS({ commit }) {
      return client.get(`/external/area/`).then((response) => {
        if (response.status === 200) {
          commit("SET_AREAS", response.content);
          commit("SET_ACCESS_CONTROL", { endpoint: "areas", hasAccess: true });
        } else if (response.status === 403) {
          commit("SET_ACCESS_CONTROL", { endpoint: "areas", hasAccess: false });
          console.warn("Access denied to areas endpoint");
        }
        return Promise.resolve();
      }).catch(error => {
        console.error("Error loading areas:", error);
        return Promise.resolve();
      });
    },
    UPDATE_PROFILE_MODE({ commit, dispatch, state }, { profileMode }) {
      // Update profile mode in state first
      commit("SET_PROFILE_MODE", profileMode);
      commit("SET_LOADING", true);
      
      // Reset access control on profile switch to re-check permissions
      commit("RESET_ACCESS_CONTROL");
      
      // Clear existing intervals to avoid duplicate requests
      if (state.data_interval) {
        clearInterval(state.data_interval);
        commit("SET_DATA_INTERVAL", null);
      }
      
      if (state.hour_data_interval) {
        clearInterval(state.hour_data_interval);
        commit("SET_HOUR_DATA_INTERVAL", null);
      }
      
      // Helper to handle API errors
      const safeDispatch = createSafeDispatch(commit, dispatch);
      
      // Load data for the new profile mode
      let dataPromise;
      
      switch (profileMode) {
        case "DISPATCHER":
          dataPromise = safeDispatch("LOAD_DATA");
          dataPromise.then(() => {
            dispatch("LOAD_DATA_RECURSIVELY");
            dispatch("SCHEDULE_HOURLY_LOAD_DATA");
          });
          break;
        case "SUPERVISOR":
          dataPromise = safeDispatch("LOAD_SUPERVISOR_DATA");
          dataPromise.then(() => {
            dispatch("LOAD_SUPERVISOR_DATA_RECURSIVELY");
            dispatch("SCHEDULE_HOURLY_LOAD_SUPERVISOR_DATA");
          });
          break;
        case "ADMIN":
          dataPromise = safeDispatch("LOAD_ADMIN_DATA");
          dataPromise.then(() => {
            dispatch("LOAD_ADMIN_DATA_RECURSIVELY");
          });
          break;
        case "HISTORIC":
          dataPromise = Promise.all([
            safeDispatch("LOAD_ADMIN_DATA"),
            safeDispatch("LOAD_HISTORIC_DATA")
          ]);
          dataPromise.then(() => {
            dispatch("LOAD_ADMIN_DATA_RECURSIVELY");
          });
          break;
        default:
          dataPromise = Promise.resolve();
      }
      
        // Timeout so tabs appear even if some APIs fail or are slow
      setTimeout(() => {
        // Default access for endpoints not yet verified
        Object.keys(state.access_control).forEach(endpoint => {
          if (state.access_control[endpoint] === null) {
            commit("SET_ACCESS_CONTROL", { endpoint, hasAccess: true });
          }
        });
        // Mark access verification complete
        commit("SET_ACCESS_CONTROL_VERIFIED");
      }, 5000); // 5 second timeout
      
      return dataPromise.finally(() => {
        commit("SET_LOADING", false);
        // Mark access verification complete after profile change
        commit("SET_ACCESS_CONTROL_VERIFIED");
      });
    },
    async GET_URGENT_PROPOSALS(_, { data }) {
      try {
        const payload = data;
        const response = await client.post(
          `/external/suggest/combinations/`,
          payload,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        let responseContent = [];
        switch (response.status) {
          case 201:
            responseContent.content = response.content;
            break;
          case 400:
            responseContent.content = [];
            break;
        }
        return responseContent;
      } catch (error) {
        return false;
      }
    },
    async EXPORT_ORDERS({ dispatch }, { orders }) {
      try {
        let payload = { services: orders };

        const { status, content } = await client.put(
          `/external/services/export/`,
          payload,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );

        const statusCode = Number(status);
        if (statusCode === 200) {
          dispatch("LOAD_ALARMS");
          return true;
        } else if (statusCode === 409) {
          return { blocked: content?.blocked || "El modelo está resolviendo. La operación está bloqueada momentáneamente" };
        }
        return false;
      } catch (error) {
        return false;
      }
    },
    async EXPORT_URGENT_ORDERS({ dispatch, state }) {
      try {
        const payload = {};

        if (state.selected_management_areas.length > 0) {
          payload.management_areas = state.selected_management_areas;
        }

        if (state.selected_containers.length > 0) {
          payload.containers = state.selected_containers;
        }

        if (state.selected_suppliers.length > 0) {
          payload.suppliers = state.selected_suppliers;
        }
        const { status, content } = await client.put(
          `/external/services/export/early/`,
          payload,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        const statusCode = Number(status);
        if (statusCode === 200) {
          dispatch("LOAD_ALARMS");
          return true;
        } else if (statusCode === 409) {
          return { blocked: content?.blocked || "El modelo está resolviendo. La operación está bloqueada momentáneamente" };
        }
        return false;
      } catch (error) {
        return false;
      }
    },
    async CONFIRM_URGENT_ORDERS({ state }) {
      try {
        const payload = {};

        if (state.selected_management_areas.length > 0) {
          payload.management_areas = state.selected_management_areas;
        }

        if (state.selected_containers.length > 0) {
          payload.containers = state.selected_containers;
        }

        if (state.selected_suppliers.length > 0) {
          payload.suppliers = state.selected_suppliers;
        }
        const { status } = await client.put(
          `/external/services/confirm/early/`,
          payload,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        return Number(status) === 200;
      } catch (error) {
        return false;
      }
    },
    async CONFIRM_START_DAY_ORDERS({ state }) {
      try {
        const payload = {};

        if (state.selected_management_areas.length > 0) {
          payload.management_areas = state.selected_management_areas;
        }

        if (state.selected_containers.length > 0) {
          payload.containers = state.selected_containers;
        }

        if (state.selected_suppliers.length > 0) {
          payload.suppliers = state.selected_suppliers;
        }
        const { status } = await client.put(
          `/external/services/confirm/rest/`,
          payload,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        return Number(status) === 200;
      } catch (error) {
        return false;
      }
    },
    async CONFIRM_ORDER(_, { order }) {
      try {
        let payload;
        let url;
        let serviceId =
          order.type === "import" ? order.import.id : order.export.id;
        switch (order.type) {
          case "combined":
            url = `?import_service_id=${order.import.id}&export_service_id=${order.export.id}`;
            payload = {
              import_service_id: order.import.id,
              export_service_id: order.export.id,
            };
            break;
          default:
            url = `?id_service=${serviceId}`;
            payload = {
              id_service: serviceId,
            };
            break;
        }
        const { status } = await client.put(
          `/external/services/confirm/${url}`,
          payload,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        return Number(status) === 200;
      } catch (error) {
        return false;
      }
    },
    async GET_SUPPLIERS_FOR_ORDER(_, { orderId }) {
      try {
        const response = await client.get(
          `/external/service/${orderId}/supplier/`,
          { _: Date.now() }
        );
        return response.content;
      } catch (error) {
        return false;
      }
    },
    async CHANGE_SUPPLIER_FOR_ORDER(_, { data }) {
      try {
        const orderId = data.orderId;
        const payload = {
          id_motive: data.reason,
          id_supplier: data.supplier,
          comment: data.comment,
        };
        
        // Add plate number when provided
        if (data.id_truck_plate) {
          payload.id_truck_plate = data.id_truck_plate;
        }
        
        // Use urgent vs non-urgent endpoint based on service
        const endpoint = data.isUrgent 
          ? `/external/service/urgent/${orderId}/supplier/`
          : `/external/service/${orderId}/supplier/`;
          
        const { status, content } = await client.post(
          endpoint,
          payload,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            Authorization: `Bearer ${session.getToken()}`
          }
        );
        const statusCode = Number(status);
        if (statusCode === 201) {
          return content;
        }
        return false;
      } catch (error) {
        return false;
      }
    },
    async CHANGE_COMBINATION(_, { data }) {
      try {
        // Copy payload without isUrgent flag
        const { isUrgent, ...payloadData } = data;
        
        // Pick endpoint based on whether the service is urgent
        const endpoint = isUrgent 
          ? `/external/routes/changes/urgent/`
          : `/external/routes/changes/`;
          
        const { status } = await client.post(
          endpoint,
          payloadData,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            Authorization: `Bearer ${session.getToken()}`
          }
        );
        return Number(status) === 201;
      } catch (error) {
        return false;
      }
    },
    async BREAK_COMBINATION(_, { data }) {
      try {
        // Extract isUrgent from payload
        const { isUrgent, supplier, ...restData } = data;
        
        const payload = {
          id_motive: restData.reason,
          import_service: restData.importOrder,
          export_service: restData.exportOrder,
          comment: restData.comment,
        };
        
        // Add supplier to payload when provided (reasons 19 and 20)
        if (supplier) {
          payload.id_supplier = supplier;
        }
        
        // Pick endpoint based on whether the service is urgent
        const endpoint = isUrgent
          ? `/external/route/break/urgent/`
          : `/external/route/break/`;
          
        const { status } = await client.post(
          endpoint,
          payload,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            Authorization: `Bearer ${session.getToken()}`
          }
        );
        return Number(status) === 201;
      } catch (error) {
        return false;
      }
    },
    async CHANGE_PASSWORD(_, { password }) {
      try {
        const payload = password;
        const { status } = await client.put(
          `/cornflow/user/${session.getUserId()}/`,
          payload,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        return Number(status) === 200;
      } catch (error) {
        return false;
      }
    },
    async FORGOT_PASSWORD(_, { email }) {
      try {
        const payload = email;
        const { status } = await client.put(
          `/cornflow/user/forgot-password/`,
          payload,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        return Number(status) === 200;
      } catch (error) {
        return false;
      }
    },
    async MANUAL_SOLVE_BY_PORT(_, { port }) {
      try {
        const { status, content } = await client.post(
          `/external/manual/solve/${port}/`,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        const statusCode = Number(status);
        if (statusCode === 201) {
          return true;
        } else if (statusCode === 409) {
          return { blocked: content?.blocked || "El modelo está resolviendo. La operación está bloqueada momentáneamente" };
        }
        return false;
      } catch (error) {
        return false;
      }
    },
    async DOWNLOAD_MANUAL_EXCEL(_, { startDate, endDate }) {
      try {
        const { status, content } = await client.get(
          `/external/weekly/summary/${startDate}/${endDate}/`,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        return { status, content };
      } catch (error) {
        return false;
      }
    },
    async UPDATE_REVIEW_CHANGES(_, { reviewChanges, approve }) {
      try {
        const url = buildReviewChangesUrl(reviewChanges, approve);
        const { status, content } = await client.post(`/external${url}/`, {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        });
        return handleReviewChangesResponse(status, content);
      } catch (error) {
        return { success: false };
      }
    },
    async ASSIGN_NEW_AREA_TO_USER(_, { area }) {
      try {
        const payload = area;
        const { status, content } = await client.post(
          `/external/user/management/areas/`,
          payload,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        return Number(status) === 201 ? content : false;
      } catch (error) {
        return false;
      }
    },
    async DELETE_ASSIGNED_AREA_TO_USER(_, { area }) {
      try {
        const { status } = await client.remove(
          `/external/user/management/areas/${area.id}/`,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        return Number(status) === 200;
      } catch (error) {
        return false;
      }
    },
    async ASSIGN_PORT_TO_DELEGATION(_, { port }) {
      try {
        const payload = port;
        const { status, content } = await client.post(
          `/external/delegations/ports/`,
          payload,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        return Number(status) === 201 ? content : false;
      } catch (error) {
        return false;
      }
    },
    async DELETE_ASSIGNED_PORT_TO_DELEGATION(_, { port }) {
      try {
        const { status } = await client.remove(
          `/external/delegations/ports/${port.id}/`,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        return Number(status) === 200;
      } catch (error) {
        return false;
      }
    },
    async EDIT_COORDINATE(_, { coordinate }) {
      try {
        const payload = {
          postal_code: coordinate.postal_code,
          province: coordinate.province,
          lat: coordinate.lat,
          long: coordinate.long,
          address: coordinate.address,
          country: coordinate.country,
          town: coordinate.town
        };

        if (coordinate.applied) {
          payload.applied = coordinate.applied ;
        }

        if (coordinate.denied) {
          payload.denied = coordinate.denied;
        }

        const { status } = await client.put(
          `/external/locations/coordinates/${coordinate.id}/`,
          payload,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        return Number(status) === 200;
      } catch (error) {
        return false;
      }
    },
    async EDIT_AREA(_, { area }) {
      try {
        const maxTrucks =
          area.max_trucks === null ||
          area.max_trucks === undefined ||
          area.max_trucks === ""
            ? null
            : (() => {
                const n = Number(area.max_trucks);
                return Number.isFinite(n) ? Math.floor(n) : null;
              })();
        const payload = {
          fleet_size: area.fleet_size || 0,
          tilt20: area.tilt20 || 0,
          tilt40: area.tilt40 || 0,
          dangerous_cargo: area.dangerous_cargo || 0,
          reefer: area.reefer || 0,
          max_trucks: maxTrucks,
          disabled: area.disabled,
          deactivated: area.deactivated,
        };
        const { status } = await client.put(
          `/external/suppliers/area/${area.id}/`,
          payload,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        return Number(status) === 200;
      } catch (error) {
        return false;
      }
    },
    async DELETE_AREA(_, { area }) {
      try {
        const { status } = await client.remove(
          `/external/suppliers/area/${area.id}/`,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        return Number(status) === 200;
      } catch (error) {
        return false;
      }
    },
    async DELETE_COORDINATE(_, { id }) {
      try {
        const { status } = await client.remove(
          `/external/locations/coordinates/${id}/`,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        return Number(status) === 200;
      } catch (error) {
        return false;
      }
    },
    async CREATE_AREA(_, { area }) {
      try {
        const mt = area.max_trucks;
        if (mt === null || mt === undefined || mt === "") {
          area.max_trucks = null;
        } else {
          const parsed = parseInt(mt, 10);
          area.max_trucks = Number.isNaN(parsed) ? null : parsed;
        }
        const payload = area;
        const { status, content } = await client.post(
          `/external/suppliers/area/`,
          payload,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        return Number(status) === 201 ? content.id : false;
      } catch (error) {
        return false;
      }
    },
    async DELETE_PREFERRED_CARRIER(_, { preferredCarrier }) {
      try {
        const { status } = await client.remove(
          `/external/factory/preferred/supplier/${preferredCarrier.id}/`,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        return Number(status) === 200;
      } catch (error) {
        return false;
      }
    },
    async ADD_PREFERRED_CARRIER(_, { preferredCarrier }) {
      // Coerce id_factory and id_supplier from string to int before send
      preferredCarrier.id_factory = parseInt(preferredCarrier.id_factory);
      preferredCarrier.id_supplier = parseInt(preferredCarrier.id_supplier);
      
      // Drop id_logistical_operator if present
      if (preferredCarrier.id_logistical_operator !== undefined) {
        delete preferredCarrier.id_logistical_operator;
      }
      
      // Ensure long_term_plan exists with a default when missing
      if (preferredCarrier.long_term_plan === undefined) {
        preferredCarrier.long_term_plan = false;
      }
      
      const payload = preferredCarrier;
      const { status, content } = await client.post(
        `/external/factory/preferred/supplier/`,
        payload,
        {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        }
      );
      return Number(status) === 201 ? content : false;
    },
    async ADD_DELEGATION(_, { delegation }) {
      // Coerce id_factory and id_supplier from string to int before send

      const payload = {
        name: delegation.name,
        road_rail_combine: delegation.road_rail_combine,
      };

      const { status, content } = await client.post(
        `/external/delegations/`,
        payload,
        {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        }
      );
      return Number(status) === 201 ? content : false;
    },
    async DELETE_DELEGATION(_, { delegation }) {
      try {
        const { status } = await client.remove(
          `/external/delegations/${delegation.id}/`,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        return Number(status) === 200;
      } catch (error) {
        console.error('Error deleting delegation:', error);
        return false;
      }
    },
    async ADD_TRUCK_PLATE(_, { truckPlate }) {
      try {
        const payload = {
          active: truckPlate.active,
          id: truckPlate.id,
          description: truckPlate.description,
          id_area: truckPlate.id_area,
          id_supplier: truckPlate.id_supplier
        };

        const { status, content } = await client.post(
          `/external/truck/plates/`,
          payload,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        return Number(status) === 201 ? content : false;
      } catch (error) {
        console.error('Error adding truck plate:', error);
        return false;
      }
    },
    async UPDATE_TRUCK_PLATE(_, { truckPlate }) {
      try {
        const payload = {
          description: truckPlate.description,
          id_area: truckPlate.id_area,
          active: truckPlate.active,
          id_supplier: truckPlate.id_supplier
        };

        const { status } = await client.put(
          `/external/truck/plates/${truckPlate.id}/`,
          payload,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        return Number(status) === 200;
      } catch (error) {
        console.error('Error updating truck plate:', error);
        return false;
      }
    },
    async DELETE_TRUCK_PLATE(_, { truckPlate }) {
      try {
        const { status } = await client.remove(
          `/external/truck/plates/${truckPlate.id}/`,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        return Number(status) === 200;
      } catch (error) {
        console.error('Error deleting truck plate:', error);
        return false;
      }
    },
    async ADD_TRUCK_DRIVER_HOLIDAY(_, { holiday }) {
      try {
        // Send only fields the backend expects
        const payload = {
          date_start: holiday.date_start,
          date_end: holiday.date_end,
          id_truck_plate: holiday.id_truck_plate,
          comment: holiday.comment,
          type: holiday.type,
        };

        const { status, content } = await client.post(
          `/external/truck/driver/holidays/`,
          payload,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        
        const statusCode = Number(status);
        if (statusCode === 201) {
          return { success: true, data: content };
        } else {
          return { success: false, error: content?.message || content?.error || 'Error al registrar las ausencias' };
        }
      } catch (error) {
        console.error('Error adding truck driver holiday:', error);
        const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Error al registrar las ausencias';
        return { success: false, error: errorMessage };
      }
    },
    async DELETE_TRUCK_DRIVER_HOLIDAY(_, { holiday }) {
      try {
        const { status, content } = await client.remove(
          `/external/truck/driver/holidays/${holiday.id}/`,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        
        const statusCode = Number(status);
        if (statusCode === 200 || statusCode === 204) {
          return { success: true };
        } else {
          return { success: false, error: content?.message || content?.error || 'Error al eliminar el registro' };
        }
      } catch (error) {
        console.error('Error deleting truck driver holiday:', error);
        const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Error al eliminar el registro';
        return { success: false, error: errorMessage };
      }
    },
    async LOAD_PORT_METRICS({ commit }, { data }) {
      try {
        if (data === null || data.length === 0) {
          commit("SET_PORT_METRICS", []);
        } else {
          const dateFrom = moment(data.date_from);
          data.date_from = dateFrom.format("YYYY-MM-DD");
          const dateTo = moment(data.date_to);
          data.date_to = dateTo.format("YYYY-MM-DD");
          const response = await client.get(
            `/external/dashboard/ports/info/${data.date_from}/${data.date_to}/`,
            {
              "Content-Type": "application/json",
              "Access-Control-Allow-Origin": "*",
            }
          );
          if (response.status === 200) {
            commit("SET_PORT_METRICS", response.content);
          }
        }
      } catch (error) {
        return false;
      }
    },
    async LOAD_SUPPLIER_METRICS({ commit }, { data }) {
      try {
        if (data === null || data.length === 0) {
          commit("SET_SUPPLIER_METRICS", []);
        } else {
          const dateFrom = moment(data.date_from);
          data.date_from = dateFrom.format("YYYY-MM-DD");
          const dateTo = moment(data.date_to);
          data.date_to = dateTo.format("YYYY-MM-DD");
          const response = await client.get(
            `/external/dashboard/suppliers/info/${data.date_from}/${data.date_to}/`,
            {
              "Content-Type": "application/json",
              "Access-Control-Allow-Origin": "*",
            }
          );
          if (response.status === 200) {
            commit("SET_SUPPLIER_METRICS", response.content);
          }
        }
      } catch (error) {
        console.error('Error loading supplier metrics:', error);
        return false;
      }
    },
    async LOAD_METRICS({ commit, state }) {
      try {
        let url = "";
        if (state.selected_management_areas.length > 0) {
          const areas = state.selected_management_areas.map(
            (area) => `areas=${area}`
          );
          url += `?${areas.join("&")}`;
        }
        
        const response = await client.get(`/external/suppliers/metrics/`, url);
        
        if (response.status === 200) {
          // Map API data to expected structures
          // 1. Savings dashboard info
          const savingsDashboardInfo = {
            km: response.content.total_km,
            base_km: response.content.base_km,
            savings: response.content.savings_percentage,
            percentage_empty_km: response.content.empty_km_percentage
          };
          commit("SET_SAVING_DASHBOARD_INFO", savingsDashboardInfo);
          
          // 2. Supplier dashboard info
          const supplierDashboardInfo = {
            mean_supplier_distance: response.content.mean_supplier_distance,
            mean_supplier_occupation_percentage: response.content.mean_supplier_occupation_percentage
          };
          commit("SET_SUPPLIER_DASHBOARD_INFO", supplierDashboardInfo);
          
          // 3. Map supplier occupation
          if (response.content.occupation_by_supplier_area) {
            commit("SET_SUPPLIERS_OCCUPATION", response.content.occupation_by_supplier_area);
          }
        }
        return Promise.resolve();
      } catch (error) {
        console.error("Error loading supplier metrics:", error);
        return Promise.resolve(); // Still resolve so callers do not hang on error
      }
    },
    async UPDATE_PREFERRED_CARRIER({ commit }, { preferredCarrier }) {
      try {
        const payload = {
          combinable: preferredCarrier.combinable,
          id_area: preferredCarrier.id_area,
          id_factory: preferredCarrier.id_factory,
          id_supplier: preferredCarrier.id_supplier,
          ignore_in_model: preferredCarrier.ignore_in_model,
          long_term_plan: preferredCarrier.long_term_plan,
          id_motive: preferredCarrier.id_motive,
          comment: preferredCarrier.comment
        };

        const { status } = await client.put(
          `/external/factory/preferred/supplier/${preferredCarrier.id}/`,
          payload,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        const statusCode = Number(status);
        if (statusCode === 200) {
          commit('UPDATE_PREFERRED_CARRIER', preferredCarrier);
          return true;
        }
        return false;
      } catch (error) {
        console.error("Error updating preferred carrier:", error);
        return false;
      }
    },
    async LOAD_LONG_TERM_ORDERS({ commit, state }) {
      try {
        // Always attempt to load if we have the flag set
        if (state.show_long_term_orders) {          
          const response = await client.get('/external/services/preferred/supplier/list/');
          
          if (response.status === 200) {
            // Process orders to match expected format
            const longTermOrders = {
              combinations: [],  // No combinations in long-term orders typically
              single_services: response.content.map(order => ({
                ...order,
                long_term_plan: true  // Key flag needed for display
              }))
            };
                        
            commit("SET_LONG_TERM_ORDERS", longTermOrders);
          } else {
            console.error("Bad response from long-term orders API:", response.status);
            commit("SET_LONG_TERM_ORDERS", { combinations: [], single_services: [] });
          }
        } else {
          // When unchecked, clear long-term orders
          commit("SET_LONG_TERM_ORDERS", { combinations: [], single_services: [] });
        }

        return Promise.resolve();
      } catch (error) {
        console.error("Error loading long-term orders:", error);
        commit("SET_LONG_TERM_ORDERS", { combinations: [], single_services: [] }); // Clear on error
        return Promise.resolve(); // Still resolve so callers do not hang on error
      }
    },
    async LOAD_PREFERRED_CARRIERS({ commit }) {
      try {
        const response = await client.get(`/external/factory/preferred/supplier/`);
        if (response.status === 200) {
          commit("SET_PREFERRED_CARRIERS", response.content);
          commit("SET_ACCESS_CONTROL", { endpoint: "preferred_carriers", hasAccess: true });
        } else if (response.status === 403) {
          commit("SET_ACCESS_CONTROL", { endpoint: "preferred_carriers", hasAccess: false });
          console.warn("Access denied to preferred carriers endpoint");
        }
        return Promise.resolve();
      } catch (error) {
        console.error("Error loading preferred carriers:", error);
        commit("SET_ACCESS_CONTROL", { endpoint: "preferred_carriers", hasAccess: false });
        return Promise.resolve();
      }
    },
    async LOAD_PREFERRED_CARRIERS_LOGISTICAL({ commit }) {
      try {
        const response = await client.get(`/external/preferred/carrier/logistical/`);
        if (response.status === 200) {
          commit("SET_PREFERRED_CARRIERS_LOGISTICAL", response.content);
          commit("SET_ACCESS_CONTROL", { endpoint: "preferred_carriers_logistical", hasAccess: true });
        } else if (response.status === 403) {
          commit("SET_ACCESS_CONTROL", { endpoint: "preferred_carriers_logistical", hasAccess: false });
          console.warn("Access denied to preferred carriers logistical endpoint");
        }
        return Promise.resolve();
      } catch (error) {
        console.error("Error loading preferred carriers logistical:", error);
        commit("SET_ACCESS_CONTROL", { endpoint: "preferred_carriers_logistical", hasAccess: false });
        return Promise.resolve();
      }
    },
    async DELETE_PREFERRED_CARRIER_LOGISTICAL(_, { preferredCarrier }) {
      try {
        const { status } = await client.remove(
          `/external/preferred/carrier/logistical/${preferredCarrier.id}/`,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        return Number(status) === 200;
      } catch (error) {
        console.error('Error deleting preferred carrier logistical:', error);
        return false;
      }
    },
    async ADD_PREFERRED_CARRIER_LOGISTICAL(_, { preferredCarrier }) {
      // Coerce id_logistical_operator and id_supplier from string to int before send
      preferredCarrier.id_logistical_operator = parseInt(preferredCarrier.id_logistical_operator);
      preferredCarrier.id_supplier = parseInt(preferredCarrier.id_supplier);
      
      // Ensure long_term_plan exists with a default when missing
      if (preferredCarrier.long_term_plan === undefined) {
        preferredCarrier.long_term_plan = false;
      }
      
      const payload = preferredCarrier;
      const { status, content } = await client.post(
        `/external/preferred/carrier/logistical/`,
        payload,
        {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        }
      );
      return Number(status) === 201 ? content : false;
    },
    async UPDATE_PREFERRED_CARRIER_LOGISTICAL({ commit }, { preferredCarrier }) {
      try {
        const payload = {
          combinable: preferredCarrier.combinable,
          id_area: preferredCarrier.id_area,
          id_logistical_operator: preferredCarrier.id_logistical_operator,
          id_supplier: preferredCarrier.id_supplier,
          ignore_in_model: preferredCarrier.ignore_in_model,
          long_term_plan: preferredCarrier.long_term_plan,
          id_motive: preferredCarrier.id_motive,
          comment: preferredCarrier.comment
        };

        const { status } = await client.put(
          `/external/preferred/carrier/logistical/${preferredCarrier.id}/`,
          payload,
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          }
        );
        const statusCode = Number(status);
        if (statusCode === 200) {
          commit('UPDATE_PREFERRED_CARRIER_LOGISTICAL', preferredCarrier);
          return true;
        }
        return false;
      } catch (error) {
        console.error("Error updating preferred carrier logistical:", error);
        return false;
      }
    },
    async LOAD_SUPPLIER_AVAILABILITY_MOTIVES({ commit }) {
      try {
        const response = await client.get(`/external/motives/availability/`);
        if (response.status === 200) {
          commit("SET_SUPPLIER_AVAILABILITY_MOTIVES", response.content);
          commit("SET_ACCESS_CONTROL", { endpoint: "supplier_availability_motives", hasAccess: true });
          return response.content;
        } else if (response.status === 403) {
          commit("SET_ACCESS_CONTROL", { endpoint: "supplier_availability_motives", hasAccess: false });
          console.warn("Access denied to supplier availability motives endpoint");
        }
        return Promise.resolve();
      } catch (error) {
        console.error("Error loading supplier availability motives:", error);
        throw error;
      }
    },
    async UPDATE_SUPPLIER_AVAILABILITY(_, { data }) {
      try {
        const response = await client.post(
          `/external/suppliers/availability/${data.supplier_id}/${data.area_id}/${data.reason_id}/`,
          { comment: data.comments },
          {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            Authorization: `Bearer ${session.getToken()}`
          }
        );

        if (response.status === 201) {
          return response.content;
        }
        return false;
      } catch (error) {
        console.error("Error updating supplier availability:", error);
        return false;
      }
    },
    async SEARCH_SERVICE(_, { serviceId, isCommercialNumber = false }) {
      try {
        const queryParams = isCommercialNumber ? { is_commercial_number: true } : {};
        const { status, content } = await client.get(
          `/external/search/service/${serviceId}/`,
          queryParams
        );
        
        return { status, content };
      } catch (error) {
        console.error("Error searching service:", error);
        return { status: 500, content: null, error };
      }
    }
  },
  mutations: {
    SET_DATA_INTERVAL(state, payload) {
      state.data_interval = payload;
    },
    SET_HOUR_DATA_INTERVAL(state, payload) {
      state.hour_data_interval = payload;
    },
    SET_ROLE(state, payload) {
      state.role = payload;
    },
    SET_PROFILE_MODE(state, payload) {
      state.profileMode = payload;
    },
    SET_LAST_RESOLUTION(state, payload) {
      state.last_resolution = payload;
      state.orderManager = new OrderManager(payload);
    },
    SET_LAST_RESOLUTION_TIME(state, payload) {
      state.last_resolution_time = payload;
    },
    SET_PORTS(state, payload) {
      state.ports = payload;
    },
    SET_MANAGEMENT_AREAS(state, payload) {
      state.management_areas = payload;
    },
    SET_PREFERRED_CARRIERS(state, payload) {
      state.preferred_carriers = payload;
    },
    SET_PREFERRED_CARRIERS_LOGISTICAL(state, payload) {
      state.preferred_carriers_logistical = payload;
    },
    SET_COORDINATES(state, payload) {
      state.coordinates = payload;
      state.coordinate_changes_formatted = payload
    },
    SET_DELEGATIONS(state, payload) {
      state.delegations = payload;
    },
    SET_DELEGATIONS_PORTS(state, payload) {
      state.delegations_ports = payload;
    },
    SET_TRUCK_PLATES(state, payload) {
      state.truck_plates = payload;
    },
    SET_TRUCK_DRIVER_HOLIDAYS(state, payload) {
      state.truck_driver_holidays = payload;
    },
    SET_SUPPLIER_ZONE_CHANGES(state, payload) {
      state.supplier_zone_changes = Array.isArray(payload) ? payload : [];
    },
    SET_ALL_PORTS(state, payload) {
      state.all_ports = payload;
    },
    SET_ALL_MANAGEMENT_AREAS(state, payload) {
      state.all_management_areas = payload;
    },
    SET_ALL_SUPPLIERS(state, payload) {
      state.all_suppliers = payload;
    },
    SET_CONTAINERS(state, payload) {
      state.containers = payload;
    },
    SET_CLIENTS(state, payload) {
      state.clients = payload;
    },
    SET_ALARMS(state, payload) {
      state.alarms = payload;
    },
    SET_USER(state, payload) {
      state.user = payload;
    },
    SET_USERS(state, payload) {
      state.users = payload;
    },
    SET_ORDER_DASHBOARD_INFO(state, payload) {
      state.orders_dashboard_info = payload;
    },
    SET_SUPPLIER_DASHBOARD_INFO(state, payload) {
      state.suppliers_dashboad_info = payload;
    },
    SET_SUPPLIERS_OCCUPATION(state, payload) {
      state.suppliers_occupation = payload;
    },
    SET_SAVING_DASHBOARD_INFO(state, payload) {
      state.savings_dashboard_info = payload;
    },
    SET_URGENT_SERVICES(state, payload) {
      state.urgent_services = new OrderManager(payload, true);
    },
    SET_REVIEW_CHANGES(state, payload) {
      state.review_changes = payload;
      state.review_changes_formatted =
        adminManager.formatReviewChanges(payload);
    },
    SET_REVIEW_CHANGES_FORMATTED(state, payload) {
      state.review_changes_formatted = payload;
    },
    SET_COORDINATE_CHANGES_FORMATTED(state, payload) {
      state.coordinate_changes_formatted = payload;
    },
    SET_REVIEW_CHANGE_FORMATTED(state, payload) {
      const index = state.review_changes_formatted.findIndex(
        (change) => change.id === payload.id
      );
      if (index !== -1) {
        state.review_changes_formatted[index] = payload;
      }
    },
    SET_COORDINATE_CHANGE_FORMATTED(state, payload) {
      const index = state.coordinate_changes_formatted.findIndex(
        (change) => change.id === payload.id
      );
      if (index !== -1) {
        state.coordinate_changes_formatted[index] = payload;
      }
    },
    SET_SUPPLIER_INFO(state, payload) {
      state.supplier_area = payload;
    },
    SET_AREAS(state, payload) {
      state.areas = payload;
    },
    SET_SELECTED_MANAGEMENT_AREAS(state, payload) {
      state.selected_management_areas = payload;
    },
    SET_SELECTED_SUPPLIERS(state, payload) {
      state.selected_suppliers = payload;
    },
    SET_SELECTED_CONTAINERS(state, payload) {
      state.selected_containers = payload;
    },
    SET_SELECTED_ORDER_ID(state, payload) {
      state.selected_order_id = payload;
    },
    SET_SELECTED_DATES(state, payload) {
      state.selected_dates = payload;
    },
    SET_SELECTED_PORT_FOR_HISTORIC_DASHBOARD(state, payload) {
      state.selected_port_for_historic_dashboard = payload;
    },
    SET_SELECTED_SUPPLIER_FOR_HISTORIC_DASHBOARD(state, payload) {
      state.selected_supplier_for_historic_dashboard = payload;
    },
    SET_URGENT_ORDERS(state, payload) {
      // Replace all checked states with the new values from payload
      if (Array.isArray(state.urgent_services.orders)) {
        state.urgent_services.orders = payload.map((order, index) => {
          // Preserve all properties from the original order except for 'checked'
          return {
            ...state.urgent_services.orders[index],
            checked: order.checked
          };
        });
      }
    },
    SET_URGENT_ORDER(state, payload) {
      const urgentOrders = state.urgent_services.orders;
      const index = payload.index;

      if (index >= 0 && index < urgentOrders.length) {
        urgentOrders[index] = payload.order;
      }
    },
    SET_CONFIRMED_ORDERS(state, payload) {
      const newOrders = [...state.orderManager.orders.confirmed];
      payload.forEach((updatedOrder) => {
        const index = newOrders.findIndex(
          (order) => order.id === updatedOrder.id
        );
        if (index !== -1) {
          newOrders[index].checked = updatedOrder.checked;
          newOrders[index].exported = updatedOrder.exported;
        }
      });
      state.orderManager.orders.confirmed = newOrders;
    },
    SET_CONFIRMED_ORDER(state, payload) {
      const confirmedOrders = [...state.orderManager.orders.confirmed];
      const index = payload.index;

      if (index >= 0 && index < confirmedOrders.length) {
        confirmedOrders[index] = { ...payload.order };
        state.orderManager.orders.confirmed = confirmedOrders;
      }
    },
    SET_NON_CONFIRMED_ORDERS(state, payload) {
      const newOrders = [...state.orderManager.orders.nonConfirmed];
      payload.forEach((updatedOrder) => {
        const index = newOrders.findIndex(
          (order) => order.id === updatedOrder.id
        );
        if (index !== -1) {
          newOrders[index].checked = updatedOrder.checked;
          newOrders[index].confirmed = updatedOrder.confirmed;
        }
      });
      state.orderManager.orders.nonConfirmed = newOrders;
    },
    SET_NON_CONFIRMED_ORDER(state, payload) {
      const nonConfirmedOrders = [...state.orderManager.orders.nonConfirmed];
      const index = payload.index;

      if (index >= 0 && index < nonConfirmedOrders.length) {
        nonConfirmedOrders[index] = { ...payload.order };
        state.orderManager.orders.nonConfirmed = nonConfirmedOrders;
      }
    },
    SET_EXPORTED_ORDERS(state, payload) {
      state.orderManager.orders.exported = payload;
    },
    ADD_CONFIRMED_ORDERS(state, payload) {
      state.orderManager.orders.confirmed.push(...payload);
    },
    ADD_EXPORTED_ORDERS(state, payload) {
      state.orderManager.orders.exported.push(...payload);
    },
    ADD_NON_CONFIRMED_ORDERS(state, payload) {
      state.orderManager.orders.nonConfirmed.push(...payload);
    },
    SET_CHANGE_SUPPLIER_MOTIVES(state, payload) {
      state.change_supplier_reasons = payload;
    },
    SET_BREAK_COMBINATION_MOTIVES(state, payload) {
      state.break_combination_reasons = payload;
    },
    SET_CHANGE_COMBINATION_MOTIVES(state, payload) {
      state.change_combination_reasons = payload;
    },
    SET_PREFERRED_CARRIER_MOTIVES(state, payload) {
      state.preferred_carrier_motives = payload;
    },
    SET_PORT_METRICS(state, payload) {
      state.port_metrics = payload;
    },
    SET_SUPPLIER_METRICS(state, payload) {
      state.supplier_metrics = payload;
    },
    UPDATE_PREFERRED_CARRIER(state, updatedCarrier) {
      const index = state.preferred_carriers.findIndex(carrier => carrier.id === updatedCarrier.id);
      if (index !== -1) {
        state.preferred_carriers.splice(index, 1, updatedCarrier);
      }
    },
    UPDATE_PREFERRED_CARRIER_LOGISTICAL(state, updatedCarrier) {
      const index = state.preferred_carriers_logistical.findIndex(carrier => carrier.id === updatedCarrier.id);
      if (index !== -1) {
        state.preferred_carriers_logistical.splice(index, 1, updatedCarrier);
      }
    },
    SET_SHOW_ONLY_EXTRA_DAY_ORDERS(state, payload) {
      state.hide_extra_day_orders = payload;
    },
    SET_LOADING(state, isLoading) {
      state.isLoading = isLoading;
      // Reset loading-operations counter when clearing loading
      if (!isLoading) {
        state.loadingOperations = 0;
      }
    },
    INCREMENT_LOADING_OPERATIONS(state) {
      state.loadingOperations++;
    },
    DECREMENT_LOADING_OPERATIONS(state) {
      if (state.loadingOperations > 0) {
        state.loadingOperations--;
      }
    },
    RESET_LOADING_OPERATIONS(state) {
      state.loadingOperations = 0;
    },
    SET_SHOW_LONG_TERM_ORDERS(state, payload) {
      state.show_long_term_orders = payload;
    },
    SET_LONG_TERM_ORDERS(state, payload) {
      state.long_term_orders = payload;
      
      // Handle long-term orders management
      if (state.orderManager?.orders) {
        // Temporary OrderManager to format long-term orders when needed
        let allLongTermOrders = [];
        
        if (state.show_long_term_orders && payload && 
            ((payload.single_services && payload.single_services.length > 0) || 
             (payload.combinations && payload.combinations.length > 0))) {
          
          const tempOrderManager = new OrderManager(payload);
          
          // Get all orders from tempOrderManager
          allLongTermOrders = [
            ...tempOrderManager.orders.exported,
            ...tempOrderManager.orders.confirmed,
            ...tempOrderManager.orders.nonConfirmed
          ];
          
          // Ensure every order has long_term_plan set to true
          allLongTermOrders.forEach(order => {
            order.long_term_plan = true;
          });
        }
        
        // Unified map of existing orders by ID for deduplication
        const allOrdersMap = new Map();
        
        // First add all existing non-long-term orders to the map
        [...state.orderManager.orders.exported, 
         ...state.orderManager.orders.confirmed, 
         ...state.orderManager.orders.nonConfirmed
        ].forEach(order => {
          if (!order.long_term_plan) {
            const orderId = order.type === "import" ? order.import.id : order.export.id;
            allOrdersMap.set(orderId, { ...order, categories: [] });
          }
        });
        
        // Process long-term orders: add new or update existing
        allLongTermOrders.forEach(longTermOrder => {
          const orderId = longTermOrder.type === "import" ? longTermOrder.import.id : longTermOrder.export.id;
          
          if (allOrdersMap.has(orderId)) {
            // Update existing order's long_term_plan flag
            const existingOrder = allOrdersMap.get(orderId);
            existingOrder.long_term_plan = true;
          } else {
            // Add new order to map
            allOrdersMap.set(orderId, { ...longTermOrder, categories: [] });
          }
        });
        
        // Now categorize each order in the map
        allOrdersMap.forEach((order) => {
          // Include long-term orders only when the flag is on
          if (order.long_term_plan && !state.show_long_term_orders) {
            return;
          }
          
          if (order.exported) {
            order.categories.push('exported');
          } else if (order.confirmed) {
            order.categories.push('confirmed');
          } else {
            order.categories.push('nonConfirmed');
          }
        });
        
        // Build final arrays with each order in the right category
        const newExported = [];
        const newConfirmed = [];
        const newNonConfirmed = [];
        
        allOrdersMap.forEach((order) => {
          if (order.categories.includes('exported')) {
            newExported.push(order);
          } else if (order.categories.includes('confirmed')) {
            newConfirmed.push(order);
          } else if (order.categories.includes('nonConfirmed')) {
            newNonConfirmed.push(order);
          }
        });
        
        // Update the orderManager with the deduplicated orders
        state.orderManager.orders = {
          exported: newExported,
          confirmed: newConfirmed,
          nonConfirmed: newNonConfirmed
        };
      }
    },
    SET_SUPPLIER_AVAILABILITY_MOTIVES(state, payload) {
      state.supplier_availability_motives = payload;
    },
    // Access control mutations
    SET_ACCESS_CONTROL(state, { endpoint, hasAccess, force = false }) {
      if (Object.prototype.hasOwnProperty.call(state.access_control, endpoint)) {
        // Only update if verification is not complete yet, or when forced
        if (!state.access_control_verified || force) {
          state.access_control[endpoint] = hasAccess;
        }
      }
    },
    SET_ACCESS_CONTROL_VERIFIED(state) {
      state.access_control_verified = true;
    },
    RESET_ACCESS_CONTROL(state) {
      Object.keys(state.access_control).forEach(key => {
        state.access_control[key] = null;
      });
      state.access_control_verified = false;
    },
  },
});

export default store;
